<template>
  <div>
    <HrPageLayout
      title="Full and Final Settlement (FNF)"
      subtitle="Manage FNF settlements and payments"
      icon="💰"
      :breadcrumb-items="[
        { label: 'Home', route: '/dashboard' },
        { label: 'Separation' },
        { label: 'FNF' }
      ]"
    >
      <template #header-actions>
        <select class="hr-select fnf-month-select" aria-label="Select Month" v-model="selectedMonth" @change="handleMonthChange">
          <option value="">Select Month</option>
          <option value="1">January</option>
          <option value="2">February</option>
          <option value="3">March</option>
          <option value="4">April</option>
          <option value="5">May</option>
          <option value="6">June</option>
          <option value="7">July</option>
          <option value="8">August</option>
          <option value="9">September</option>
          <option value="10">October</option>
          <option value="11">November</option>
          <option value="12">December</option>
        </select>
        <button type="button" class="hr-btn hr-btn-primary" @click="exportReport">
          <i class="bx bx-download"></i> Export Report
        </button>
        <button type="button" class="hr-btn hr-btn-success" @click="openCreateFnfModal">
          <i class="bx bx-plus"></i> Create FNF
        </button>
      </template>

      <div class="separation-fnf-content">
        <div v-if="message" class="sep-toast" :class="message.includes('success') ? 'success' : 'error'">
          <strong>{{ message.includes('success') ? 'Success' : 'Error' }}</strong>
          <span>{{ message }}</span>
          <button type="button" class="sep-toast-close" @click="message = ''">&times;</button>
        </div>

        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-money"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Total FNF</p>
            <h2 class="hr-metric-value">{{ statistics.totalFNF }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Completed FNF</p>
            <h2 class="hr-metric-value">{{ statistics.completedFNF }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Pending FNF</p>
            <h2 class="hr-metric-value">{{ statistics.pendingFNF }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-loader-alt"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Processing FNF</p>
            <h2 class="hr-metric-value">{{ statistics.processingFNF }}</h2>
            </div>
          </div>
        </div>

        <div class="hr-chart-card">
          <div class="hr-chart-header hr-flex-between">
            <div class="sep-filters">
              <div class="hr-search-wrap">
                <input type="text" class="hr-input" placeholder="Search by employee name, ID..." v-model="searchKeyword" @input="handleSearch">
                <i class="bx bx-search-alt search-icon"></i>
              </div>
              <div class="hr-filter-group">
                <label class="hr-form-label">Department</label>
                <select class="hr-select" style="width: 200px;" v-model="selectedDepartment" @change="handleDepartmentChange">
                  <option value="">All Departments</option>
                  <option v-for="department in departments" :key="department.id" :value="department.id">
                    {{ department.departmentName }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="hr-chart-body">
            <div v-if="isLoading" class="hr-loading-wrap">
              <div class="hr-spinner"></div>
              <p>Loading...</p>
            </div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover hr-table-striped">
                <thead>
                  <tr>
                    <th>Employee Name</th>
                    <th>Separation Type</th>
                    <th>Last Working Day</th>
                    <th>Department</th>
                    <th>FNF Status</th>
                    <th>FNF Amount</th>
                    <th>Payment Status</th>
                    <th>Settlement Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in fnfData" :key="item.id">
                    <td>
                      <div>{{ item.employeeName }}</div>
                      <small class="hr-text-muted">ID: {{ item.employeeId }}</small>
                    </td>
                    <td>
                      <span class="hr-badge" :class="getSeparationTypeBadgeClass(item.separationType)">{{ item.separationType }}</span>
                    </td>
                    <td>{{ formatDate(item.lastWorkingDay) }}</td>
                    <td>
                      <div>{{ item.departmentName }}</div>
                      <small class="hr-text-muted">ID: {{ item.departmentId }}</small>
                    </td>
                    <td>
                      <div class="fnf-status-cell">
                        <i class="bx fnf-status-icon" :class="getFnfStatusIconClass(item.fnfStatus)"></i>
                        <span>{{ item.fnfStatus }}</span>
                      </div>
                    </td>
                    <td>
                      <span class="fnf-amount" :class="parseFloat(item.fnfAmount) >= 0 ? 'payment-positive' : 'payment-negative'">
                        {{ parseFloat(item.fnfAmount) >= 0 ? '+' : '' }}{{ formatCurrency(item.fnfAmount) }}
                      </span>
                      <span v-if="item.fnfStatus !== 'Completed'" class="hr-badge hr-badge-muted">Est.</span>
                    </td>
                    <td>
                      <span :class="'hr-badge ' + getPaymentStatusBadgeClass(item.paymentStatus)">{{ item.paymentStatus }}</span>
                      <div class="hr-text-muted hr-small hr-mt-1" v-if="item.paymentStatus === 'Paid' && item.paymentDate">
                        {{ formatDate(item.paymentDate) }}
                      </div>
                    </td>
                    <td>{{ item.settlementDate ? formatDate(item.settlementDate) : '--' }}</td>
                    <td>
                      <div class="fnf-actions-cell">
                        <template v-if="item.fnfStatus === 'Completed'">
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="viewFnf(item)">
                            <i class="bx bx-file"></i> View
                          </button>
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-info" @click="printFnf(item)">
                            <i class="bx bx-printer"></i> Print
                          </button>
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-ghost" @click="downloadPdf(item)">
                            <i class="bx bx-download"></i> PDF
                          </button>
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-ghost" @click="emailSettlement(item)">
                            <i class="bx bx-envelope"></i> Email
                          </button>
                        </template>
                        <template v-else-if="item.fnfStatus === 'Processing'">
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-success" @click="completeFnf(item)">
                            <i class="bx bx-check"></i> Complete
                          </button>
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-warning" @click="editFnf(item)">
                            <i class="bx bx-edit"></i> Edit
                          </button>
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-ghost" @click="viewDetails(item)">View</button>
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-ghost" @click="cancelFnf(item)">Cancel</button>
                        </template>
                        <template v-else>
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="processFnf(item)">
                            <i class="bx bx-cog"></i> Process
                          </button>
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-warning" @click="editFnf(item)">
                            <i class="bx bx-edit"></i> Edit
                          </button>
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-ghost" @click="viewDetails(item)">View</button>
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-ghost" @click="openApprovalModal(item)">Approvals</button>
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-ghost" @click="cancelFnf(item)">Cancel</button>
                        </template>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="fnfData.length === 0">
                    <td colspan="9" class="hr-text-center hr-py-4 hr-text-muted">No FNF records found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </HrPageLayout>

    <!-- Create FNF Modal -->
      <div class="modal fade" id="modal-create-fnf" tabindex="-1" aria-labelledby="createFnfModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="createFnfModalLabel">
                {{ isEditMode ? 'Edit Full and Final Settlement' : 'Create Full and Final Settlement' }}
              </h5>
              <button
                  type="button"
                  class="btn-close"
                  data-bs-dismiss="modal"
                  @click="closeCreateFnfModal($event)"
                  aria-label="Close"
              ></button>
            </div>
            <div class="modal-body">
              <form id="createFnfForm" @submit.prevent="submitFnf">
                <div class="row mb-3">
                  <div class="col-md-6">
                    <label for="selectEmployee" class="form-label">Select Employee</label>
                    <select class="form-select" id="selectEmployee" v-model="fnfForm.employeeId" required>
                      <option value="">Select Employee</option>
                      <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                        {{ employee.name }} (ID: {{ employee.id }})
                      </option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label for="separationType" class="form-label">Separation Type</label>
                    <select class="form-select" id="separationType" v-model="fnfForm.separationType" required>
                      <option value="">Select Type</option>
                      <option value="Resignation">Resignation</option>
                      <option value="Transfers">Transfers</option>
                      <option value="Dismissal">Dismissal</option>
                      <option value="Death">Death</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-md-6">
                    <label for="separationDate" class="form-label">Separation Date</label>
                    <input type="date" class="form-control" id="separationDate" v-model="fnfForm.separationDate" required>
                  </div>
                  <div class="col-md-6">
                    <label for="lastWorkingDay" class="form-label">Last Working Day</label>
                    <input type="date" class="form-control" id="lastWorkingDay" v-model="fnfForm.lastWorkingDay" required>
                  </div>
                </div>

                <div class="card mb-3">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Earnings</h6>
                  </div>
                  <div class="card-body">
                    <div class="row mb-3">
                      <div class="col-md-6">
                        <label for="salaryDue" class="form-label">Salary Due</label>
                        <div class="input-group">
                          <span class="input-group-text">{{ getCurrencySymbol() }}</span>
                          <input type="number" step="0.01" class="form-control" id="salaryDue" placeholder="0.00" v-model="fnfForm.earnings.salaryDue">
                        </div>
                      </div>
                      <div class="col-md-6">
                        <label for="bonusDue" class="form-label">Bonus Due</label>
                        <div class="input-group">
                          <span class="input-group-text">{{ getCurrencySymbol() }}</span>
                          <input type="number" step="0.01" class="form-control" id="bonusDue" placeholder="0.00" v-model="fnfForm.earnings.bonusDue">
                        </div>
                      </div>
                    </div>
                    <div class="row mb-3">
                      <div class="col-md-6">
                        <label for="leaveEncashment" class="form-label">Leave Encashment</label>
                        <div class="input-group">
                          <span class="input-group-text">{{ getCurrencySymbol() }}</span>
                          <input type="number" step="0.01" class="form-control" id="leaveEncashment" placeholder="0.00" v-model="fnfForm.earnings.leaveEncashment">
                        </div>
                      </div>
                      <div class="col-md-6">
                        <label for="gratuity" class="form-label">Gratuity</label>
                        <div class="input-group">
                          <span class="input-group-text">{{ getCurrencySymbol() }}</span>
                          <input type="number" step="0.01" class="form-control" id="gratuity" placeholder="0.00" v-model="fnfForm.earnings.gratuity">
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-6">
                        <label for="otherEarnings" class="form-label">Other Earnings</label>
                        <div class="input-group">
                          <span class="input-group-text">{{ getCurrencySymbol() }}</span>
                          <input type="number" step="0.01" class="form-control" id="otherEarnings" placeholder="0.00" v-model="fnfForm.earnings.otherEarnings">
                        </div>
                      </div>
                      <div class="col-md-6">
                        <label for="earningNotes" class="form-label">Notes</label>
                        <input type="text" class="form-control" id="earningNotes" placeholder="Describe other earnings" v-model="fnfForm.earnings.notes">
                      </div>
                    </div>
                  </div>
                </div>

                <div class="card mb-3">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Deductions</h6>
                  </div>
                  <div class="card-body">
                    <div class="row mb-3">
                      <div class="col-md-6">
                        <label for="advanceSalary" class="form-label">Advance Salary</label>
                        <div class="input-group">
                          <span class="input-group-text">{{ getCurrencySymbol() }}</span>
                          <input type="number" step="0.01" class="form-control" id="advanceSalary" placeholder="0.00" v-model="fnfForm.deductions.advanceSalary">
                        </div>
                      </div>
                      <div class="col-md-6">
                        <label for="loan" class="form-label">Loan Outstanding</label>
                        <div class="input-group">
                          <span class="input-group-text">{{ getCurrencySymbol() }}</span>
                          <input type="number" step="0.01" class="form-control" id="loan" placeholder="0.00" v-model="fnfForm.deductions.loan">
                        </div>
                      </div>
                    </div>
                    <div class="row mb-3">
                      <div class="col-md-6">
                        <label for="taxDeduction" class="form-label">Tax Deduction</label>
                        <div class="input-group">
                          <span class="input-group-text">{{ getCurrencySymbol() }}</span>
                          <input type="number" step="0.01" class="form-control" id="taxDeduction" placeholder="0.00" v-model="fnfForm.deductions.taxDeduction">
                        </div>
                      </div>
                      <div class="col-md-6">
                        <label for="recoveries" class="form-label">Recoveries (Assets/Equipment)</label>
                        <div class="input-group">
                          <span class="input-group-text">{{ getCurrencySymbol() }}</span>
                          <input type="number" step="0.01" class="form-control" id="recoveries" placeholder="0.00" v-model="fnfForm.deductions.recoveries">
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-6">
                        <label for="otherDeductions" class="form-label">Other Deductions</label>
                        <div class="input-group">
                          <span class="input-group-text">{{ getCurrencySymbol() }}</span>
                          <input type="number" step="0.01" class="form-control" id="otherDeductions" placeholder="0.00" v-model="fnfForm.deductions.otherDeductions">
                        </div>
                      </div>
                      <div class="col-md-6">
                        <label for="deductionNotes" class="form-label">Notes</label>
                        <input type="text" class="form-control" id="deductionNotes" placeholder="Describe other deductions" v-model="fnfForm.deductions.notes">
                      </div>
                    </div>
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-md-6">
                    <div class="card">
                      <div class="card-header bg-light">
                        <h6 class="mb-0">Settlement Summary</h6>
                      </div>
                      <div class="card-body">
                        <div class="d-flex justify-content-between mb-2">
                          <span>Total Earnings:</span>
                          <span class="fw-bold">{{ formatCurrency(calculateTotalEarnings()) }}</span>
                        </div>
                        <div class="d-flex justify-content-between mb-2">
                          <span>Total Deductions:</span>
                          <span class="fw-bold">{{ formatCurrency(calculateTotalDeductions()) }}</span>
                        </div>
                        <hr>
                        <div class="d-flex justify-content-between">
                          <span>Net Payable:</span>
                          <span class="fw-bold" :class="calculateNetPayable() >= 0 ? 'text-success' : 'text-danger'">
                      {{ formatCurrency(calculateNetPayable()) }}
                    </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="card">
                      <div class="card-header bg-light">
                        <h6 class="mb-0">Additional Details</h6>
                      </div>
                      <div class="card-body">
                        <div class="mb-3">
                          <label for="paymentMethod" class="form-label">Payment Method</label>
                          <select class="form-select" id="paymentMethod" v-model="fnfForm.paymentMethod">
                            <option value="bank">Bank Transfer</option>
                            <option value="check">Check</option>
                            <option value="cash">Cash</option>
                          </select>
                        </div>
                        <div class="mb-3">
                          <label for="settlementNotes" class="form-label">Notes</label>
                          <textarea class="form-control" id="settlementNotes" rows="2" placeholder="Any additional notes" v-model="fnfForm.notes"></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mb-3">
                  <label for="uploadDocuments" class="form-label">Upload Supporting Documents</label>
                  <input type="file" class="form-control" id="uploadDocuments" multiple @change="handleFileUpload">
                  <small class="text-muted">Upload resignation letter, clearance forms, loan documents, etc.</small>
                </div>
              </form>
            </div>
            <div class="modal-footer">
              <button
                  type="button"
                  class="btn btn-secondary"
                  data-bs-dismiss="modal"
                  @click="closeCreateFnfModal($event)"
              >
                Cancel
              </button>
              <button type="button" class="btn btn-primary" @click="calculateSettlement">Calculate</button>
              <button type="button" class="btn btn-success" @click="submitFnf()" :disabled="isSubmitting">
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                {{ isEditMode ? 'Update' : 'Save' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- View FNF Modal -->
      <!-- View FNF Modal -->
      <div class="modal fade" id="modal-view-fnf" tabindex="-1" aria-labelledby="viewFnfModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="viewFnfModalLabel">Full and Final Settlement Details</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" @click="closeModalFromEvent($event)" aria-label="Close"></button>
            </div>
            <div class="modal-body" v-if="selectedFnf">
              <div class="row mb-4">
                <div class="col-md-6 mb-3">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Employee Information</h6>
                    </div>
                    <div class="card-body">
                      <table class="table table-borderless settlement-info">
                        <tbody>
                          <tr>
                            <td class="fw-bold">Name:</td>
                            <td>{{ selectedFnf.employeeName }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold">ID:</td>
                            <td>{{ selectedFnf.employeeId }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold">Department:</td>
                            <td>{{ selectedFnf.departmentName }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold">Position:</td>
                            <td>{{ selectedFnf.position || 'N/A' }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold">Join Date:</td>
                            <td>{{ selectedFnf.joinDate ? formatDate(selectedFnf.joinDate) : 'N/A' }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Separation Information</h6>
                    </div>
                    <div class="card-body">
                      <table class="table table-borderless settlement-info">
                        <tbody>
                          <tr>
                            <td class="fw-bold">Type:</td>
                            <td><span class="badge rounded-pill" :class="{
                              'bg-success': selectedFnf.separationType === 'Resignation',
                              'bg-danger': selectedFnf.separationType === 'Death',
                              'bg-primary': selectedFnf.separationType === 'Transfers',
                              'bg-warning': selectedFnf.separationType === 'Dismissal',
                              'bg-secondary': selectedFnf.separationType === 'Other'
                            }">{{ selectedFnf.separationType }}</span></td>
                          </tr>
                          <tr>
                            <td class="fw-bold">Last Working Day:</td>
                            <td>{{ formatDate(selectedFnf.lastWorkingDay) }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold">Settlement Date:</td>
                            <td>{{ selectedFnf.settlementDate ? formatDate(selectedFnf.settlementDate) : 'N/A' }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold">Payment Date:</td>
                            <td>{{ selectedFnf.paymentDate ? formatDate(selectedFnf.paymentDate) : 'N/A' }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold">Status:</td>
                            <td><span class="badge" :class="{
                              'bg-success': selectedFnf.fnfStatus === 'Completed',
                              'bg-warning text-dark': selectedFnf.fnfStatus === 'Pending',
                              'bg-primary': selectedFnf.fnfStatus === 'Processing'
                            }">{{ selectedFnf.fnfStatus }}</span></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row mb-4">
                <div class="col-md-6 mb-3">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Earnings</h6>
                    </div>
                    <div class="card-body">
                      <table class="table table-sm">
                        <tbody>
                        <tr>
                          <td>Salary Due</td>
                          <td class="text-end">{{ formatCurrency(selectedFnf.earnings.salaryDue) }}</td>
                        </tr>
                        <tr>
                          <td>Bonus</td>
                          <td class="text-end">{{ formatCurrency(selectedFnf.earnings.bonusDue) }}</td>
                        </tr>
                        <tr>
                          <td>Leave Encashment</td>
                          <td class="text-end">{{ formatCurrency(selectedFnf.earnings.leaveEncashment) }}</td>
                        </tr>
                        <tr>
                          <td>Gratuity</td>
                          <td class="text-end">{{ formatCurrency(selectedFnf.earnings.gratuity) }}</td>
                        </tr>
                        <tr v-if="parseFloat(selectedFnf.earnings.otherEarnings) > 0">
                          <td>Other Earnings</td>
                          <td class="text-end">{{ formatCurrency(selectedFnf.earnings.otherEarnings) }}</td>
                        </tr>
                        <tr class="table-light">
                          <td class="fw-bold">Total Earnings</td>
                          <td class="text-end fw-bold">{{ formatCurrency(selectedFnf.totalEarnings) }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div class="col-md-6 mb-3">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Deductions</h6>
                    </div>
                    <div class="card-body">
                      <table class="table table-sm">
                        <tbody>
                        <tr>
                          <td>Advance Salary</td>
                          <td class="text-end">{{ formatCurrency(selectedFnf.deductions.advanceSalary) }}</td>
                        </tr>
                        <tr>
                          <td>Tax Deduction</td>
                          <td class="text-end">{{ formatCurrency(selectedFnf.deductions.taxDeduction) }}</td>
                        </tr>
                        <tr>
                          <td>Loan Outstanding</td>
                          <td class="text-end">{{ formatCurrency(selectedFnf.deductions.loan) }}</td>
                        </tr>
                        <tr>
                          <td>Recoveries (Company Assets)</td>
                          <td class="text-end">{{ formatCurrency(selectedFnf.deductions.recoveries) }}</td>
                        </tr>
                        <tr v-if="parseFloat(selectedFnf.deductions.otherDeductions) > 0">
                          <td>Other Deductions</td>
                          <td class="text-end">{{ formatCurrency(selectedFnf.deductions.otherDeductions) }}</td>
                        </tr>
                        <tr class="table-light">
                          <td class="fw-bold">Total Deductions</td>
                          <td class="text-end fw-bold">{{ formatCurrency(selectedFnf.totalDeductions) }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row mb-4">
                <div class="col-md-12">
                  <div class="card">
                    <div class="card-header bg-success text-white">
                      <h6 class="mb-0">Settlement Summary</h6>
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-8 offset-md-2">
                          <table class="table table-bordered">
                            <tbody>
                            <tr>
                              <td class="fw-bold">Total Earnings</td>
                              <td class="text-end">{{ formatCurrency(selectedFnf.totalEarnings) }}</td>
                            </tr>
                            <tr>
                              <td class="fw-bold">Total Deductions</td>
                              <td class="text-end">{{ formatCurrency(selectedFnf.totalDeductions) }}</td>
                            </tr>
                            <tr class="table-success">
                              <td class="fw-bold">Net Amount Payable</td>
                              <td class="text-end fw-bold">{{ formatCurrency(selectedFnf.netPayable) }}</td>
                            </tr>
                            <tr>
                              <td class="fw-bold">Payment Method</td>
                              <td class="text-end">{{ formatPaymentMethod(selectedFnf.paymentMethod) }}</td>
                            </tr>
                            <tr>
                              <td class="fw-bold">Payment Status</td>
                              <td class="text-end"><span class="badge" :class="{
                          'bg-success': selectedFnf.paymentStatus === 'Paid',
                          'bg-warning text-dark': selectedFnf.paymentStatus === 'Pending',
                          'bg-danger': selectedFnf.paymentStatus === 'Due from Employee'
                        }">{{ selectedFnf.paymentStatus }}</span></td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-12">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Notes & Approvals</h6>
                    </div>
                    <div class="card-body">
                      <div class="mb-3">
                        <label class="fw-bold">Settlement Notes:</label>
                        <p>{{ selectedFnf.notes || 'No notes provided.' }}</p>
                      </div>
                      <div class="row">
                        <div class="col-md-4 mb-2">
                          <div class="d-flex align-items-center">
                            <div class="me-2"><i class="mdi" :class="selectedFnf.approvals && selectedFnf.approvals.finance ? 'mdi-check-circle text-success' : 'mdi-clock-outline text-warning'"></i></div>
                            <div>
                              <div class="fw-bold">Finance Approval</div>
                              <small class="text-muted" v-if="selectedFnf.approvals && selectedFnf.approvals.finance">
                                By: {{ selectedFnf.approvals.financeBy || 'Finance Manager' }} on {{ selectedFnf.approvals.financeDate ? formatDate(selectedFnf.approvals.financeDate) : 'N/A' }}
                              </small>
                              <small class="text-muted" v-else>Pending approval</small>
                            </div>
                          </div>
                        </div>
                        <div class="col-md-4 mb-2">
                          <div class="d-flex align-items-center">
                            <div class="me-2"><i class="mdi" :class="selectedFnf.approvals && selectedFnf.approvals.hr ? 'mdi-check-circle text-success' : 'mdi-clock-outline text-warning'"></i></div>
                            <div>
                              <div class="fw-bold">HR Approval</div>
                              <small class="text-muted" v-if="selectedFnf.approvals && selectedFnf.approvals.hr">
                                By: {{ selectedFnf.approvals.hrBy || 'HR Manager' }} on {{ selectedFnf.approvals.hrDate ? formatDate(selectedFnf.approvals.hrDate) : 'N/A' }}
                              </small>
                              <small class="text-muted" v-else>Pending approval</small>
                            </div>
                          </div>
                        </div>
                        <div class="col-md-4 mb-2">
                          <div class="d-flex align-items-center">
                            <div class="me-2"><i class="mdi" :class="selectedFnf.approvals && selectedFnf.approvals.management ? 'mdi-check-circle text-success' : 'mdi-clock-outline text-warning'"></i></div>
                            <div>
                              <div class="fw-bold">Management Approval</div>
                              <small class="text-muted" v-if="selectedFnf.approvals && selectedFnf.approvals.management">
                                By: {{ selectedFnf.approvals.managementBy || 'Manager' }} on {{ selectedFnf.approvals.managementDate ? formatDate(selectedFnf.approvals.managementDate) : 'N/A' }}
                              </small>
                              <small class="text-muted" v-else>Pending approval</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Close</button>
              <button type="button" class="btn btn-info" @click="printFnf(selectedFnf)"><i class="mdi mdi-printer me-1"></i> Print</button>
              <button type="button" class="btn btn-primary" @click="downloadPdf(selectedFnf)"><i class="mdi mdi-download me-1"></i> Download PDF</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Process FNF Modal -->
      <div class="modal fade" id="modal-process-fnf" tabindex="-1" aria-labelledby="processFnfModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="processFnfModalLabel">Process FNF Settlement</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" @click="closeModalFromEvent($event)" aria-label="Close"></button>
            </div>
            <div class="modal-body" v-if="selectedFnf">
              <div class="alert alert-info">
                <i class="mdi mdi-information-outline me-2"></i>
                Please verify the data and approve the settlement to proceed with payment.
              </div>

              <div class="row mb-3">
                <div class="col-md-6">
                  <div class="card mb-3">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Employee Details</h6>
                    </div>
                    <div class="card-body">
                      <table class="table table-borderless settlement-info">
                        <tbody>
                          <tr>
                            <td class="fw-bold">Name:</td>
                            <td>{{ selectedFnf.employeeName }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold">ID:</td>
                            <td>{{ selectedFnf.employeeId }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold">Department:</td>
                            <td>{{ selectedFnf.departmentName }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold">Separation Type:</td>
                            <td><span class="badge rounded-pill" :class="{
                              'bg-success': selectedFnf.separationType === 'Resignation',
                              'bg-danger': selectedFnf.separationType === 'Death',
                              'bg-primary': selectedFnf.separationType === 'Transfers',
                              'bg-warning': selectedFnf.separationType === 'Dismissal',
                              'bg-secondary': selectedFnf.separationType === 'Other'
                            }">{{ selectedFnf.separationType }}</span></td>
                          </tr>
                          <tr>
                            <td class="fw-bold">Last Working Day:</td>
                            <td>{{ formatDate(selectedFnf.lastWorkingDay) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Payment Information</h6>
                    </div>
                    <div class="card-body">
                      <div class="mb-3">
                        <label for="paymentDate" class="form-label">Payment Date</label>
                        <input type="date" class="form-control" id="paymentDate" v-model="processFnfForm.paymentDate">
                      </div>
                      <div class="mb-3">
                        <label for="processPaymentMethod" class="form-label">Payment Method</label>
                        <select class="form-select" id="processPaymentMethod" v-model="processFnfForm.paymentMethod">
                          <option value="bank">Bank Transfer</option>
                          <option value="check">Check</option>
                          <option value="cash">Cash</option>
                        </select>
                      </div>
                      <div class="mb-3">
                        <label for="referenceNo" class="form-label">Reference Number</label>
                        <input type="text" class="form-control" id="referenceNo" placeholder="Transaction or Check number" v-model="processFnfForm.referenceNo">
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="card mb-3">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Settlement Summary</h6>
                    </div>
                    <div class="card-body">
                      <table class="table table-bordered">
                        <tbody>
                        <tr>
                          <td class="fw-bold">Total Earnings</td>
                          <td class="text-end">{{ formatCurrency(selectedFnf.totalEarnings) }}</td>
                        </tr>
                        <tr>
                          <td class="fw-bold">Total Deductions</td>
                          <td class="text-end">{{ formatCurrency(selectedFnf.totalDeductions) }}</td>
                        </tr>
                        <tr class="table-success">
                          <td class="fw-bold">Net Amount Payable</td>
                          <td class="text-end fw-bold">{{ formatCurrency(selectedFnf.netPayable) }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Clearance Status</h6>
                    </div>
                    <div class="card-body">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <span><i class="mdi" :class="selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.financeClearance ? 'mdi-check-circle text-success' : 'mdi-alert-circle text-warning'"></i> Finance Department</span>
                        <span class="badge" :class="selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.financeClearance ? 'bg-success' : 'bg-warning text-dark'">
                    {{ selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.financeClearance ? 'Cleared' : 'Pending' }}
                  </span>
                      </div>
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <span><i class="mdi" :class="selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.itClearance ? 'mdi-check-circle text-success' : 'mdi-alert-circle text-warning'"></i> IT Department</span>
                        <span class="badge" :class="selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.itClearance ? 'bg-success' : 'bg-warning text-dark'">
                    {{ selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.itClearance ? 'Cleared' : 'Pending' }}
                  </span>
                      </div>
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <span><i class="mdi" :class="selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.adminClearance ? 'mdi-check-circle text-success' : 'mdi-alert-circle text-warning'"></i> Admin Department</span>
                        <span class="badge" :class="selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.adminClearance ? 'bg-success' : 'bg-warning text-dark'">
                    {{ selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.adminClearance ? 'Cleared' : 'Pending' }}
                  </span>
                      </div>
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <span><i class="mdi" :class="selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.hrClearance ? 'mdi-check-circle text-success' : 'mdi-alert-circle text-warning'"></i> HR Department</span>
                        <span class="badge" :class="selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.hrClearance ? 'bg-success' : 'bg-warning text-dark'">
                    {{ selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.hrClearance ? 'Cleared' : 'Pending' }}
                  </span>
                      </div>
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <span><i class="mdi" :class="selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.deptClearance ? 'mdi-check-circle text-success' : 'mdi-alert-circle text-warning'"></i> Department Head</span>
                        <span class="badge" :class="selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.deptClearance ? 'bg-success' : 'bg-warning text-dark'">
                    {{ selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.deptClearance ? 'Cleared' : 'Pending' }}
                  </span>
                      </div>
                      <div class="d-flex justify-content-between align-items-center">
                        <span><i class="mdi" :class="selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.securityClearance ? 'mdi-check-circle text-success' : 'mdi-alert-circle text-warning'"></i> Security Department</span>
                        <span class="badge" :class="selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.securityClearance ? 'bg-success' : 'bg-warning text-dark'">
                    {{ selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.securityClearance ? 'Cleared' : 'Pending' }}
                  </span>
                      </div>
                      <div v-if="selectedFnf.clearanceInfo && selectedFnf.clearanceInfo.clearanceNotes" class="mt-3 pt-2 border-top">
                        <div class="text-muted small">
                          <strong>Clearance Notes:</strong> {{ selectedFnf.clearanceInfo.clearanceNotes }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-12">
                  <div class="mb-3">
                    <label for="processingNotes" class="form-label">Processing Notes</label>
                    <textarea class="form-control" id="processingNotes" rows="3" placeholder="Enter any additional notes for this settlement" v-model="processFnfForm.notes"></textarea>
                  </div>

                  <div class="form-check mb-3">
                    <input class="form-check-input" type="checkbox" id="confirmVerification" v-model="processFnfForm.verified">
                    <label class="form-check-label" for="confirmVerification">
                      I confirm that all information has been verified and is correct
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Close</button>
              <button type="button" class="btn btn-warning me-2" @click="holdProcessFnf">Hold</button>
              <button type="button" class="btn btn-success" @click="approveProcessFnf" :disabled="!processFnfForm.verified || isProcessing">
                <span v-if="isProcessing" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Approve & Process Payment
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Email FNF Modal -->
      <div class="modal fade" id="modal-email-fnf" tabindex="-1" aria-labelledby="emailFnfModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-md">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="emailFnfModalLabel">Email Settlement Details</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" @click="closeModalFromEvent($event)" aria-label="Close"></button>
            </div>
            <div class="modal-body" v-if="selectedFnf">
              <div v-if="emailForm.success" class="text-center py-4">
                <div class="avatar-sm mx-auto mb-3 bg-success rounded-circle">
                  <i class="mdi mdi-check-bold font-size-24 avatar-title text-white"></i>
                </div>
                <h5>Email Sent Successfully!</h5>
                <p class="text-muted">Settlement details have been emailed to the employee.</p>
              </div>

              <div v-else>
                <div class="alert alert-info mb-4">
                  <div class="d-flex">
                    <div class="me-3">
                      <i class="mdi mdi-information-outline font-size-24"></i>
                    </div>
                    <div>
                      <h5 class="alert-heading">Sending Settlement Details</h5>
                      <p class="mb-0">
                        You are about to email the settlement details to <strong>{{ selectedFnf.employeeName }}</strong>.
                      </p>
                    </div>
                  </div>
                </div>

                <div v-if="emailForm.error" class="alert alert-danger">
                  {{ emailForm.error }}
                </div>

                <form @submit.prevent="sendFnfEmail">
                  <div class="mb-3">
                    <label class="form-label">Email Template</label>
                    <div class="btn-group w-100" role="group">
                      <input type="radio" class="btn-check" name="emailTemplate" id="templateDefault" value="default"
                             v-model="emailForm.template" autocomplete="off" checked>
                      <label class="btn btn-outline-primary" for="templateDefault">Default</label>

                      <input type="radio" class="btn-check" name="emailTemplate" id="templateSummary" value="summary"
                             v-model="emailForm.template" autocomplete="off">
                      <label class="btn btn-outline-primary" for="templateSummary">Summary</label>

                      <input type="radio" class="btn-check" name="emailTemplate" id="templateDetailed" value="detailed"
                             v-model="emailForm.template" autocomplete="off">
                      <label class="btn btn-outline-primary" for="templateDetailed">Detailed</label>
                    </div>
                  </div>

                  <div class="mb-3">
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" id="attachPdf" v-model="emailForm.includeAttachment">
                      <label class="form-check-label" for="attachPdf">
                        Attach PDF document
                      </label>
                    </div>
                  </div>

                  <div class="mb-3">
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" id="ccManager" v-model="emailForm.ccToManager">
                      <label class="form-check-label" for="ccManager">
                        CC to department manager
                      </label>
                    </div>
                  </div>

                  <div class="alert alert-light border mt-3">
                    <div class="mb-0">
                      <small class="text-muted">
                        <i class="mdi mdi-information-outline me-1"></i>
                        The email will be sent to the registered email address of the employee. Make sure the email address is correct and up to date.
                      </small>
                    </div>
                  </div>
                </form>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Close</button>
              <button
                  v-if="!emailForm.success"
                  type="button"
                  class="btn btn-primary"
                  @click="sendFnfEmail"
                  :disabled="emailForm.sending"
              >
                <span v-if="emailForm.sending" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Send Email
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- FNF Approval Modal -->
    <div class="modal fade" id="modal-fnf-approval" tabindex="-1" aria-labelledby="fnfApprovalModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="fnfApprovalModalLabel">Approve Full and Final Settlement</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" @click="closeModalFromEvent($event)" aria-label="Close"></button>
          </div>
          <div class="modal-body" v-if="selectedFnf">
            <div class="mb-4">
              <div class="alert alert-info">
                <div class="d-flex">
                  <div class="me-2">
                    <i class="mdi mdi-information-outline fs-4"></i>
                  </div>
                  <div>
                    <p class="mb-1"><strong>{{ selectedFnf.employeeName }}</strong> (ID: {{ selectedFnf.employeeId }})</p>
                    <p class="mb-0">Last Working Day: {{ formatDate(selectedFnf.lastWorkingDay) }}</p>
                    <p class="mb-0">Department: {{ selectedFnf.departmentName }}</p>
                    <p class="mb-0">Net Payable: {{ formatCurrency(selectedFnf.netPayable) }}</p>
                  </div>
                </div>
              </div>
            </div>

            <ul class="nav nav-tabs nav-tabs-custom" role="tablist">
              <li class="nav-item">
                <a class="nav-link" :class="{ 'active': activeApprovalTab === 'finance' }" @click="activeApprovalTab = 'finance'" data-bs-toggle="tab" href="#finance-approval" role="tab">
                  <span class="d-block d-sm-none"><i :class="selectedFnf.approvals && selectedFnf.approvals.finance ? 'mdi mdi-check-circle text-success' : 'mdi mdi-clock-outline text-warning'"></i></span>
                  <span class="d-none d-sm-block">
                <i :class="selectedFnf.approvals && selectedFnf.approvals.finance ? 'mdi mdi-check-circle text-success me-1' : 'mdi mdi-clock-outline text-warning me-1'"></i>
                Finance Approval
              </span>
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ 'active': activeApprovalTab === 'hr' }" @click="activeApprovalTab = 'hr'" data-bs-toggle="tab" href="#hr-approval" role="tab">
                  <span class="d-block d-sm-none"><i :class="selectedFnf.approvals && selectedFnf.approvals.hr ? 'mdi mdi-check-circle text-success' : 'mdi mdi-clock-outline text-warning'"></i></span>
                  <span class="d-none d-sm-block">
                <i :class="selectedFnf.approvals && selectedFnf.approvals.hr ? 'mdi mdi-check-circle text-success me-1' : 'mdi mdi-clock-outline text-warning me-1'"></i>
                HR Approval
              </span>
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ 'active': activeApprovalTab === 'management' }" @click="activeApprovalTab = 'management'" data-bs-toggle="tab" href="#management-approval" role="tab">
                  <span class="d-block d-sm-none"><i :class="selectedFnf.approvals && selectedFnf.approvals.management ? 'mdi mdi-check-circle text-success' : 'mdi mdi-clock-outline text-warning'"></i></span>
                  <span class="d-none d-sm-block">
                <i :class="selectedFnf.approvals && selectedFnf.approvals.management ? 'mdi mdi-check-circle text-success me-1' : 'mdi mdi-clock-outline text-warning me-1'"></i>
                Management Approval
              </span>
                </a>
              </li>
            </ul>

            <div class="tab-content p-3">
              <!-- Finance Approval Tab -->
              <div class="tab-pane" :class="{ 'active': activeApprovalTab === 'finance' }" id="finance-approval" role="tabpanel">
                <div class="d-flex align-items-center mb-3">
                  <div class="me-3">
                <span class="badge rounded-pill" :class="selectedFnf.approvals && selectedFnf.approvals.finance ? 'bg-success' : 'bg-warning'">
                  {{ selectedFnf.approvals && selectedFnf.approvals.finance ? 'Approved' : 'Pending approval' }}
                </span>
                  </div>
                  <div class="text-muted small" v-if="selectedFnf.approvals && selectedFnf.approvals.finance">
                    Approved by {{ selectedFnf.approvals.financeBy || 'Finance Manager' }} on {{ selectedFnf.approvals.financeDate ? formatDate(selectedFnf.approvals.financeDate) : 'N/A' }}
                  </div>
                </div>

                <h5>Finance Department Approval</h5>
                <p>Finance department needs to verify all financial calculations and outstanding amounts before approval.</p>

                <div class="alert alert-info">
                  <div class="d-flex">
                    <div class="me-2">
                      <i class="mdi mdi-information-outline"></i>
                    </div>
                    <div>
                      <p class="mb-1">Please verify:</p>
                      <ul class="mb-0">
                        <li>All outstanding loans have been accounted for</li>
                        <li>Salary calculations are accurate</li>
                        <li>Tax deductions are properly calculated</li>
                        <li>All company assets have been properly valued</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div v-if="!(selectedFnf.approvals && selectedFnf.approvals.finance)">
                  <div class="mb-3">
                    <label for="financeNotes" class="form-label">Approval Notes</label>
                    <textarea class="form-control" id="financeNotes" rows="3" v-model="approvalForm.notes" placeholder="Add any notes or comments regarding the approval"></textarea>
                  </div>

                  <button type="button" class="btn btn-success" @click="approveFinance" :disabled="isApproving">
                    <span v-if="isApproving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Approve Finance Clearance
                  </button>
                </div>
              </div>

              <!-- HR Approval Tab -->
              <div class="tab-pane" :class="{ 'active': activeApprovalTab === 'hr' }" id="hr-approval" role="tabpanel">
                <div class="d-flex align-items-center mb-3">
                  <div class="me-3">
                <span class="badge rounded-pill" :class="selectedFnf.approvals && selectedFnf.approvals.hr ? 'bg-success' : 'bg-warning'">
                  {{ selectedFnf.approvals && selectedFnf.approvals.hr ? 'Approved' : 'Pending approval' }}
                </span>
                  </div>
                  <div class="text-muted small" v-if="selectedFnf.approvals && selectedFnf.approvals.hr">
                    Approved by {{ selectedFnf.approvals.hrBy || 'HR Manager' }} on {{ selectedFnf.approvals.hrDate ? formatDate(selectedFnf.approvals.hrDate) : 'N/A' }}
                  </div>
                </div>

                <h5>HR Department Approval</h5>
                <p>HR department must verify employee clearance and documentation before approval.</p>

                <div class="alert alert-info">
                  <div class="d-flex">
                    <div class="me-2">
                      <i class="mdi mdi-information-outline"></i>
                    </div>
                    <div>
                      <p class="mb-1">Please verify:</p>
                      <ul class="mb-0">
                        <li>Exit interview has been conducted</li>
                        <li>All company policies regarding separation have been followed</li>
                        <li>Employee has completed all required documentation</li>
                        <li>All dues from benefit plans have been settled</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div v-if="!(selectedFnf.approvals && selectedFnf.approvals.hr)">
                  <div class="mb-3">
                    <label for="hrNotes" class="form-label">Approval Notes</label>
                    <textarea class="form-control" id="hrNotes" rows="3" v-model="approvalForm.notes" placeholder="Add any notes or comments regarding the approval"></textarea>
                  </div>

                  <button type="button" class="btn btn-success" @click="approveHR" :disabled="isApproving || !(selectedFnf.approvals && selectedFnf.approvals.finance)">
                    <span v-if="isApproving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Approve HR Clearance
                  </button>

                  <div class="text-muted mt-2" v-if="!(selectedFnf.approvals && selectedFnf.approvals.finance)">
                    <i class="mdi mdi-information-outline me-1"></i> Finance approval is required before HR approval
                  </div>
                </div>
              </div>

              <!-- Management Approval Tab -->
              <div class="tab-pane" :class="{ 'active': activeApprovalTab === 'management' }" id="management-approval" role="tabpanel">
                <div class="d-flex align-items-center mb-3">
                  <div class="me-3">
                <span class="badge rounded-pill" :class="selectedFnf.approvals && selectedFnf.approvals.management ? 'bg-success' : 'bg-warning'">
                  {{ selectedFnf.approvals && selectedFnf.approvals.management ? 'Approved' : 'Pending approval' }}
                </span>
                  </div>
                  <div class="text-muted small" v-if="selectedFnf.approvals && selectedFnf.approvals.management">
                    Approved by {{ selectedFnf.approvals.managementBy || 'Management' }} on {{ selectedFnf.approvals.managementDate ? formatDate(selectedFnf.approvals.managementDate) : 'N/A' }}
                  </div>
                </div>

                <h5>Management Approval</h5>
                <p>Final approval from management is required to complete the FNF process.</p>

                <div class="alert alert-info">
                  <div class="d-flex">
                    <div class="me-2">
                      <i class="mdi mdi-information-outline"></i>
                    </div>
                    <div>
                      <p class="mb-1">Please verify:</p>
                      <ul class="mb-0">
                        <li>Finance and HR approvals have been obtained</li>
                        <li>All compliance requirements have been met</li>
                        <li>Final settlement amount is correct and approved</li>
                        <li>All company policies have been followed</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div v-if="!(selectedFnf.approvals && selectedFnf.approvals.management)">
                  <div class="mb-3">
                    <label for="managementNotes" class="form-label">Approval Notes</label>
                    <textarea class="form-control" id="managementNotes" rows="3" v-model="approvalForm.notes" placeholder="Add any notes or comments regarding the approval"></textarea>
                  </div>

                  <button type="button" class="btn btn-success" @click="approveManagement" :disabled="isApproving || !(selectedFnf.approvals && selectedFnf.approvals.finance && selectedFnf.approvals.hr)">
                    <span v-if="isApproving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Approve Management Clearance
                  </button>

                  <div class="text-muted mt-2" v-if="!(selectedFnf.approvals && selectedFnf.approvals.finance && selectedFnf.approvals.hr)">
                    <i class="mdi mdi-information-outline me-1"></i> Both Finance and HR approvals are required before Management approval
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Close</button>
            <button
                type="button"
                class="btn btn-primary"
                v-if="selectedFnf && selectedFnf.approvals && selectedFnf.approvals.finance && selectedFnf.approvals.hr && selectedFnf.approvals.management"
                @click="completeFnfAfterApproval"
            >
              <i class="mdi mdi-check-all me-1"></i> Complete FNF
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl } from "@/api";

export default {
  name: "SeparationFNF",
  components: { HrPageLayout },
  data() {
    return {
      // Data variables
      isEditMode: false,
      editingFnfId: null, // Track the ID of the record being edited
      date: '',
      selectedMonth: '3', // Default to March
      selectedDepartment: '',
      searchKeyword: '',
      fnfData: [],
      employees: [],
      departments: [],
      isLoading: false,
      isSubmitting: false,
      isProcessing: false,
      message: '',

      // Store references to Bootstrap modals
      createFnfModal: null,
      viewFnfModal: null,
      processFnfModal: null,

      // Track selected record
      selectedFnf: null,

      // Form data for creating FNF
      fnfForm: {
        employeeId: '',
        separationType: '',
        separationDate: new Date().toISOString().substr(0, 10),
        lastWorkingDay: new Date().toISOString().substr(0, 10),
        earnings: {
          salaryDue: 0,
          bonusDue: 0,
          leaveEncashment: 0,
          gratuity: 0,
          otherEarnings: 0,
          notes: ''
        },
        deductions: {
          advanceSalary: 0,
          loan: 0,
          taxDeduction: 0,
          recoveries: 0,
          otherDeductions: 0,
          notes: ''
        },
        paymentMethod: 'bank',
        notes: '',
        documents: []
      },

      // Form data for processing FNF
      processFnfForm: {
        paymentDate: new Date().toISOString().substr(0, 10),
        paymentMethod: 'bank',
        referenceNo: '',
        notes: '',
        verified: false
      },

      // Statistics
      statistics: {
        totalFNF: 0,
        completedFNF: 0,
        pendingFNF: 0,
        processingFNF: 0
      },

      // Add these new properties
      approvalModal: null,
      activeApprovalTab: 'finance',
      approvalForm: {
        notes: '',
        approverName: ''
      },
      isApproving: false,

      emailModal: null,
      emailForm: {
        template: 'default',
        includeAttachment: true,
        ccToManager: false,
        sending: false,
        success: false,
        error: ''
      }

    };
  },
  // Add this to your Vue component
  computed: {
    // Currency code from the selected department
    currentCurrencyCode() {
      return this.getCurrentCurrencyCode();
    },

    // Currency symbol for display
    currencySymbol() {
      return this.getCurrencySymbol();
    }
  },

// This makes the code more reactive, ensuring UI updates when department changes
  watch: {
    selectedDepartment() {
      // When department changes, force refresh of any currency displays
      this.$forceUpdate();
    }
  },

  mounted() {
    // Initialize the component
    this.getDepartments();
    this.getEmployeesList();
    this.getFnfData();

    // Initialize Bootstrap modals
    this.$nextTick(() => {
      if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
        const createEl = document.getElementById('modal-create-fnf');
        const viewEl = document.getElementById('modal-view-fnf');
        const processEl = document.getElementById('modal-process-fnf');

        if (createEl) this.createFnfModal = new bootstrap.Modal(createEl);
        if (viewEl) this.viewFnfModal = new bootstrap.Modal(viewEl);
        if (processEl) this.processFnfModal = new bootstrap.Modal(processEl);
      }
    });
  },

  methods: {

    // Fetch FNF data
    getFnfData() {
      this.isLoading = true;
      const data = new FormData();
      data.append("function", "getFnfData");

      // Add filters if selected
      if (this.selectedMonth) {
        data.append("month", this.selectedMonth);
      }
      if (this.selectedDepartment) {
        data.append("department", this.selectedDepartment);
      }
      if (this.searchKeyword) {
        data.append("keyword", this.searchKeyword);
      }

      execute(data, authUrl)
          .then((res) => {
            this.isLoading = false;
            if (res.data.success) {
              // Check if data exists and has the expected structure
              if (res.data.data && Array.isArray(res.data.data.fnfRecords)) {
                // Update statistics from the metrics in the response
                if (res.data.data.metrics) {
                  this.statistics = {
                    totalFNF: res.data.data.metrics.totalFNF || 0,
                    completedFNF: res.data.data.metrics.completedFNF || 0,
                    pendingFNF: res.data.data.metrics.pendingFNF || 0,
                    processingFNF: res.data.data.metrics.processingFNF || 0
                  };
                }

                // Process the FNF data
                this.fnfData = res.data.data.fnfRecords.map((item) => {
                  // Parse earnings JSON if it's a string
                  let parsedEarnings = item.earnings;
                  if (typeof parsedEarnings === 'string') {
                    try {
                      parsedEarnings = JSON.parse(parsedEarnings);
                    } catch (e) {
                      console.error("Error parsing earnings:", e);
                      parsedEarnings = {
                        salaryDue: 0,
                        bonusDue: 0,
                        leaveEncashment: 0,
                        gratuity: 0,
                        otherEarnings: 0,
                        notes: ''
                      };
                    }
                  }

                  // Parse deductions JSON if it's a string
                  let parsedDeductions = item.deductions;
                  if (typeof parsedDeductions === 'string') {
                    try {
                      parsedDeductions = JSON.parse(parsedDeductions);
                    } catch (e) {
                      console.error("Error parsing deductions:", e);
                      parsedDeductions = {
                        advanceSalary: 0,
                        loan: 0,
                        taxDeduction: 0,
                        recoveries: 0,
                        otherDeductions: 0,
                        notes: ''
                      };
                    }
                  }

                  // Parse clearance info if it exists
                  // Parse clearance info if it exists
                  let parsedClearanceInfo = item.clearanceInfo;
                  if (typeof parsedClearanceInfo === 'string') {
                    try {
                      parsedClearanceInfo = JSON.parse(parsedClearanceInfo);
                    } catch (e) {
                      console.error("Error parsing clearance info:", e);
                      parsedClearanceInfo = {
                        financeClearance: false,
                        itClearance: false,
                        adminClearance: false,
                        hrClearance: false,
                        deptClearance: false,
                        securityClearance: false,
                        clearanceNotes: ""
                      };
                    }
                  }

                  // Parse approvals info if it exists
                  let parsedApprovals = item.approvals;
                  if (typeof parsedApprovals === 'string') {
                    try {
                      parsedApprovals = JSON.parse(parsedApprovals);
                    } catch (e) {
                      console.error("Error parsing approvals info:", e);
                      parsedApprovals = {
                        finance: false,
                        hr: false,
                        management: false
                      };
                    }
                  }

                  // Calculate totals if they're not already provided
                  const totalEarnings = item.totalEarnings || this.calculateTotalAmount(parsedEarnings);
                  const totalDeductions = item.totalDeductions || this.calculateTotalAmount(parsedDeductions);
                  const netPayable = item.netPayable || (totalEarnings - totalDeductions);

                  return {
                    id: item.id,
                    employeeId: item.employeeId,
                    employeeName: item.employeeName || "N/A",
                    position: item.position || "",
                    joinDate: item.joinDate || null,
                    separationType: item.separationType || "N/A",
                    separationDate: item.separationDate || null,
                    lastWorkingDay: item.lastWorkingDay || null,
                    departmentId: item.departmentId || "",
                    departmentName: item.departmentName || "N/A",
                    fnfStatus: item.fnfStatus || "Pending",
                    fnfAmount: netPayable,
                    paymentStatus: item.paymentStatus || "Pending",
                    paymentDate: item.paymentDate || null,
                    settlementDate: item.settlementDate || null,
                    earnings: parsedEarnings,
                    deductions: parsedDeductions,
                    clearance: item.clearance ? parsedClearance : null,
                    clearanceInfo: parsedClearanceInfo,
                    approvals: parsedApprovals,
                    paymentMethod: item.paymentMethod || "bank",
                    referenceNo: item.referenceNo || "",
                    notes: item.notes || "",
                    documents: item.documents || [],
                    totalEarnings: totalEarnings,
                    totalDeductions: totalDeductions,
                    netPayable: netPayable,
                    createdAt: item.createdAt,
                    updatedAt: item.updatedAt,

                  };
                });
              } else {
                this.fnfData = [];
                console.log("No FNF data found or incorrect data format");
              }
            } else {
              this.message = res.data.message || "Failed to fetch FNF data";
              this.fnfData = [];
            }
          })
          .catch((e) => {
            this.isLoading = false;
            this.message = e.message || "An error occurred while fetching FNF data";
            this.fnfData = [];
          });
    },

    // Get departments list
    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              if (res.data.data && Array.isArray(res.data.data.departments)) {
                this.departments = res.data.data.departments;
              } else {
                console.log("No departments data found or incorrect data format");
              }
            } else {
              this.message = res.data.message || "Failed to fetch departments";
            }
          })
          .catch((e) => {
            this.message = e.message || "An error occurred while fetching departments";
          });
    },

    // Get employees list for dropdown
    getEmployeesList() {
      const data = new FormData();
      data.append("function", "getEmployees");
      // No status filter: show all employees that backend returns

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              const list = Array.isArray(res.data.data?.employees) ? res.data.data.employees : [];
              this.employees = list.map(employee => {
                const middleName = employee.middleName ? ` ${employee.middleName} ` : ' ';
                return {
                  id: employee.id,
                  name: `${employee.firstName}${middleName}${employee.lastName}`,
                  value: employee.id,
                  department: employee.departmentID,
                  designation: employee.designation
                };
              });
            } else {
              this.message = res.data.message || "Failed to fetch employees";
            }
          })
          .catch((e) => {
            this.message = e.message || "An error occurred while fetching employees";
          });
    },

    // Handle month selection change
    handleMonthChange() {
      this.getFnfData();
    },

    // Handle department selection change
    handleDepartmentChange() {
      this.getFnfData();
    },

    // Handle search input
    handleSearch() {
      this.getFnfData();
    },

    openCreateFnfModal() {
      // Use the instance created in mounted()
      if (this.createFnfModal && typeof this.createFnfModal.show === 'function') {
        this.createFnfModal.show();
      } else if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
        const el = document.getElementById('modal-create-fnf');
        if (el) {
          this.createFnfModal = new bootstrap.Modal(el);
          this.createFnfModal.show();
        }
      }
    },

    closeCreateFnfModal(e) {
      // Prefer the locally stored Bootstrap modal instance
      if (this.createFnfModal && typeof this.createFnfModal.hide === 'function') {
        this.createFnfModal.hide();
        return;
      }

      // Fallback to global helper (from main.js mixin) if available
      if (typeof this.closeModalFromEvent === 'function') {
        this.closeModalFromEvent(e);
      } else if (e && e.target && e.target.closest) {
        const modalEl = e.target.closest('.modal');
        if (modalEl) {
          modalEl.classList.remove('show');
          modalEl.style.display = 'none';
        }
      }
    },

    getSeparationTypeBadgeClass(type) {
      if (!type) return 'hr-badge-secondary';
      if (type === 'Resignation') return 'hr-badge-success';
      if (type === 'Death') return 'hr-badge-danger';
      if (type === 'Transfers') return 'hr-badge-primary';
      if (type === 'Dismissal') return 'hr-badge-warning';
      return 'hr-badge-secondary';
    },

    getPaymentStatusBadgeClass(status) {
      if (!status) return 'hr-badge-secondary';
      if (status === 'Paid') return 'hr-badge-success';
      if (status === 'Pending') return 'hr-badge-warning';
      if (status === 'Due from Employee') return 'hr-badge-danger';
      return 'hr-badge-secondary';
    },

    getFnfStatusIconClass(status) {
      if (!status) return 'bx-time-five';
      if (status === 'Pending') return 'bx-time-five fnf-status-pending';
      if (status === 'Approved') return 'bx-check fnf-status-approved';
      if (status === 'Completed') return 'bx-check-circle fnf-status-completed';
      if (status === 'Cancelled') return 'bx-x-circle fnf-status-cancelled';
      return 'bx-time-five';
    },

    // Handle file uploads for FNF
    handleFileUpload(event) {
      this.fnfForm.documents = event.target.files;
    },

    // Calculate total amount from an object's numeric properties
    calculateTotalAmount(obj) {
      return Object.values(obj)
          .filter(value => typeof value === 'number')
          .reduce((sum, value) => sum + value, 0);
    },

    // Calculate total earnings
    calculateTotalEarnings() {
      const earnings = this.fnfForm.earnings;
      return (
          parseFloat(earnings.salaryDue || 0) +
          parseFloat(earnings.bonusDue || 0) +
          parseFloat(earnings.leaveEncashment || 0) +
          parseFloat(earnings.gratuity || 0) +
          parseFloat(earnings.otherEarnings || 0)
      );
    },

    // Calculate total deductions
    calculateTotalDeductions() {
      const deductions = this.fnfForm.deductions;
      return (
          parseFloat(deductions.advanceSalary || 0) +
          parseFloat(deductions.loan || 0) +
          parseFloat(deductions.taxDeduction || 0) +
          parseFloat(deductions.recoveries || 0) +
          parseFloat(deductions.otherDeductions || 0)
      );
    },

    // Calculate net payable
    calculateNetPayable() {
      return this.calculateTotalEarnings() - this.calculateTotalDeductions();
    },

    // Calculate settlement details
    calculateSettlement() {
      const netPayable = this.calculateNetPayable();
      this.message = `Settlement calculated. Net payable amount: ${this.formatCurrency(netPayable)}`;
    },

    closeAllModals() {
      // First close all modals using Bootstrap's API
      const modals = document.querySelectorAll('.modal.show');
      modals.forEach(modal => {
        const modalInstance = bootstrap.Modal.getInstance(modal);
        if (modalInstance) {
          modalInstance.hide();
        }
      });

      // Then remove any lingering backdrop elements
      const backdrops = document.querySelectorAll('.modal-backdrop');
      backdrops.forEach(backdrop => {
        backdrop.remove();
      });

      // Finally, fix the body by removing modal-open class and inline styles
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('padding-right');
      document.body.style.removeProperty('overflow');
    },

    // Submit FNF form
    submitFnf() {
      // Validate required fields
      if (!this.fnfForm.employeeId || !this.fnfForm.separationType ||
          !this.fnfForm.separationDate || !this.fnfForm.lastWorkingDay) {
        this.message = "Please fill all required fields";
        return;
      }

      this.isSubmitting = true;

      // Calculate totals
      const totalEarnings = this.calculateTotalEarnings();
      const totalDeductions = this.calculateTotalDeductions();
      const netPayable = this.calculateNetPayable();

      // Find the selected employee to include their details
      const selectedEmployee = this.employees.find(emp => emp.id === this.fnfForm.employeeId);

      const data = new FormData();

      // Set the appropriate function based on whether we're editing or creating
      if (this.isEditMode && this.editingFnfId) {
        data.append("function", "updateFnf");
        data.append("fnfId", this.editingFnfId);
      } else {
        data.append("function", "createFnf");
      }

      // Common data for both create and update
      data.append("employeeId", this.fnfForm.employeeId);
      data.append("employeeName", selectedEmployee ? selectedEmployee.name : "");
      data.append("separationType", this.fnfForm.separationType);
      data.append("separationDate", this.fnfForm.separationDate);
      data.append("lastWorkingDay", this.fnfForm.lastWorkingDay);
      data.append("earnings", JSON.stringify(this.fnfForm.earnings));
      data.append("deductions", JSON.stringify(this.fnfForm.deductions));
      data.append("paymentMethod", this.fnfForm.paymentMethod);
      data.append("notes", this.fnfForm.notes);
      data.append("totalEarnings", totalEarnings);
      data.append("totalDeductions", totalDeductions);
      data.append("netPayable", netPayable);

      // Only set these fields for new records, not when updating
      if (!this.isEditMode) {
        data.append("fnfStatus", "Pending");
        data.append("paymentStatus", netPayable >= 0 ? "Pending" : "Due from Employee");
      }

      // Include department info if available
      if (selectedEmployee && selectedEmployee.department) {
        data.append("departmentId", selectedEmployee.department);
      }

      // Include position/designation if available
      if (selectedEmployee && selectedEmployee.designation) {
        data.append("position", selectedEmployee.designation);
      }

      // Append documents if any
      if (this.fnfForm.documents.length > 0) {
        for (let i = 0; i < this.fnfForm.documents.length; i++) {
          data.append("documents[]", this.fnfForm.documents[i]);
        }
      }

      execute(data, authUrl)
          .then((res) => {
            this.isSubmitting = false;
            this.closeAllModals();
            if (res.data.success) {
              // Show appropriate success message
              this.message = this.isEditMode ?
                  "FNF record updated successfully" :
                  "FNF record created successfully";

              // Refresh data
              this.getFnfData();

              // Reset form and edit mode
              this.resetFnfForm();
            } else {
              this.message = res.data.message ||
                  (this.isEditMode ? "Failed to update FNF record" : "Failed to create FNF record");
            }
          })
          .catch((e) => {
            this.isSubmitting = false;
            this.message = e.message || "An error occurred";
          });
    },

    // Reset FNF form
    resetFnfForm() {
      // Reset edit mode
      this.isEditMode = false;
      this.editingFnfId = null;

      // Reset form fields
      this.fnfForm = {
        employeeId: '',
        separationType: '',
        separationDate: new Date().toISOString().substr(0, 10),
        lastWorkingDay: new Date().toISOString().substr(0, 10),
        earnings: {
          salaryDue: 0,
          bonusDue: 0,
          leaveEncashment: 0,
          gratuity: 0,
          otherEarnings: 0,
          notes: ''
        },
        deductions: {
          advanceSalary: 0,
          loan: 0,
          taxDeduction: 0,
          recoveries: 0,
          otherDeductions: 0,
          notes: ''
        },
        paymentMethod: 'bank',
        notes: '',
        documents: []
      };
      // Reset file input
      const fileInput = document.getElementById('uploadDocuments');
      if (fileInput) fileInput.value = '';
    },


      // View FNF record
    viewFnf(fnf) {
      this.selectedFnf = fnf;
      if (this.viewFnfModal) {
        this.viewFnfModal.show();
      }
    },

    // Process FNF
    processFnf(fnf) {
      this.selectedFnf = fnf;

      // Set default values for processing form
      this.processFnfForm = {
        paymentDate: new Date().toISOString().substr(0, 10),
        paymentMethod: fnf.paymentMethod || 'bank',
        referenceNo: '',
        notes: '',
        verified: false
      };

      if (this.processFnfModal) {
        this.processFnfModal.show();
      }
    },

    // Hold processing FNF
    holdProcessFnf() {
      if (!this.selectedFnf) return;

      const data = new FormData();
      data.append("function", "updateFnfStatus");
      data.append("fnfId", this.selectedFnf.id);
      data.append("status", "Pending");
      data.append("notes", this.processFnfForm.notes);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              // Close modal
              if (this.processFnfModal) {
                this.processFnfModal.hide();
              }

              // Show success message
              this.message = "FNF status updated to Pending";

              // Refresh data
              this.getFnfData();
            } else {
              this.message = res.data.message || "Failed to update FNF status";
            }
          })
          .catch((e) => {
            this.message = e.message || "An error occurred while updating FNF status";
          });
    },

    // Approve and process FNF payment
    approveProcessFnf() {
      if (!this.selectedFnf || !this.processFnfForm.verified) return;

      this.isProcessing = true;

      const data = new FormData();
      data.append("function", "processFnf");
      data.append("fnfId", this.selectedFnf.id);
      data.append("status", "Processing");
      data.append("paymentDate", this.processFnfForm.paymentDate);
      data.append("paymentMethod", this.processFnfForm.paymentMethod);
      data.append("referenceNo", this.processFnfForm.referenceNo);
      data.append("notes", this.processFnfForm.notes);

      execute(data, authUrl)
          .then((res) => {
            this.isProcessing = false;
            if (res.data.success) {
              // Close modal
              if (this.processFnfModal) {
                this.processFnfModal.hide();
              }

              // Show success message
              this.message = "FNF status updated to Processing";

              // Refresh data
              this.getFnfData();
            } else {
              this.message = res.data.message || "Failed to process FNF";
            }
          })
          .catch((e) => {
            this.isProcessing = false;
            this.message = e.message || "An error occurred while processing FNF";
          });
    },

    // Complete FNF processing
    completeFnf(fnf) {
      if (!confirm('Are you sure you want to mark this FNF as Completed?')) return;

      const data = new FormData();
      data.append("function", "completeFnf");
      data.append("fnfId", fnf.id);
      data.append("settlementDate", new Date().toISOString().substr(0, 10));
      data.append("paymentStatus", "Paid");
      data.append("paymentDate", new Date().toISOString().substr(0, 10));

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              // Show success message
              this.message = "FNF marked as Completed";

              // Refresh data
              this.getFnfData();
            } else {
              this.message = res.data.message || "Failed to complete FNF";
            }
          })
          .catch((e) => {
            this.message = e.message || "An error occurred while completing FNF";
          });
    },

    // Edit FNF record
    editFnf(fnf) {
      // Set edit mode flag
      this.isEditMode = true;
      this.editingFnfId = fnf.id;

      // Populate form with existing data
      this.fnfForm = {
        employeeId: fnf.employeeId,
        separationType: fnf.separationType,
        separationDate: fnf.separationDate,
        lastWorkingDay: fnf.lastWorkingDay,
        earnings: { ...fnf.earnings },
        deductions: { ...fnf.deductions },
        paymentMethod: fnf.paymentMethod,
        notes: fnf.notes,
        documents: []
      };

      // Show modal
      if (this.createFnfModal) {
        this.createFnfModal.show();
      }
    },

    // Cancel FNF record
    cancelFnf(fnf) {
      if (!confirm('Are you sure you want to cancel this FNF record?')) return;

      const data = new FormData();
      data.append("function", "cancelFnf");
      data.append("fnfId", fnf.id);

      execute(data, authUrl)
          .then((res) => {
            this.closeAllModals();
            if (res.data.success) {
              // Show success message
              this.message = "FNF record cancelled successfully";

              // Refresh data
              this.getFnfData();
            } else {
              this.message = res.data.message || "Failed to cancel FNF record";
            }
          })
          .catch((e) => {
            this.message = e.message || "An error occurred while cancelling FNF record";
          });
    },

    // View details of FNF
    viewDetails(fnf) {
      this.viewFnf(fnf);
    },

    // Print FNF settlement
    // Print FNF settlement
    printFnf(fnf) {
      // Create a new window for printing
      const printWindow = window.open('', '_blank');

      if (!printWindow) {
        this.message = "Unable to open print window. Please check your popup blocker settings.";
        return;
      }

      // Get currency and date formatting functions
      const formatCurrency = this.formatCurrency;
      const formatDate = this.formatDate;
      const formatPaymentMethod = this.formatPaymentMethod;

      // Create HTML content for printing
      const printContent = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>FNF Settlement - ${fnf.employeeName}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 20px;
          color: #333;
        }
        .header {
          text-align: center;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #eee;
        }
        .header h1 {
          font-size: 24px;
          margin-bottom: 5px;
          color: #333;
        }
        .header p {
          font-size: 12px;
          color: #666;
          margin: 5px 0;
        }
        .meta-info {
          text-align: right;
          font-size: 12px;
          color: #666;
          margin-bottom: 15px;
        }
        .section {
          margin-bottom: 20px;
        }
        .section-title {
          font-size: 16px;
          font-weight: bold;
          margin-bottom: 10px;
          padding-bottom: 5px;
          border-bottom: 1px solid #eee;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        table.bordered {
          border: 1px solid #ddd;
        }
        th, td {
          padding: 8px;
          text-align: left;
          border-bottom: 1px solid #ddd;
        }
        th {
          background-color: #f5f5f5;
          font-weight: bold;
        }
        .amount {
          text-align: right;
        }
        .total-row {
          font-weight: bold;
          background-color: #f9f9f9;
        }
        .positive {
          color: green;
        }
        .negative {
          color: red;
        }
        .summary-table {
          width: 70%;
          margin: 0 auto;
          border: 1px solid #ddd;
        }
        .summary-table th {
          background-color: #4b6cb7;
          color: white;
        }
        .badge {
          display: inline-block;
          padding: 3px 7px;
          font-size: 12px;
          font-weight: bold;
          border-radius: 12px;
          color: white;
        }
        .badge-success {
          background-color: #28a745;
        }
        .badge-primary {
          background-color: #007bff;
        }
        .badge-warning {
          background-color: #ffc107;
          color: #212529;
        }
        .badge-danger {
          background-color: #dc3545;
        }
        .badge-secondary {
          background-color: #6c757d;
        }
        .clearance-table {
          width: 100%;
        }
        .clearance-table td {
          width: 50%;
        }
        .info-box {
          display: inline-block;
          margin-right: 20px;
          margin-bottom: 10px;
        }
        .info-box .label {
          font-weight: bold;
          font-size: 12px;
        }
        .info-box .value {
          font-size: 14px;
        }
        .notes {
          border: 1px solid #ddd;
          padding: 10px;
          border-radius: 4px;
          background-color: #f9f9f9;
        }
        .footer {
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #eee;
          font-size: 12px;
          color: #666;
          text-align: center;
        }
        .page-break {
          page-break-before: always;
        }
        .signature-area {
          margin-top: 40px;
          display: flex;
          justify-content: space-between;
        }
        .signature-box {
          width: 45%;
        }
        .signature-line {
          margin-top: 50px;
          border-top: 1px solid #333;
          padding-top: 5px;
          text-align: center;
        }
        @media print {
          body {
            padding: 0;
            margin: 10mm;
          }
          .no-print {
            display: none;
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>FULL AND FINAL SETTLEMENT</h1>
        <p>Settlement Statement</p>
      </div>

      <div class="meta-info">
        <p>Generated on: ${formatDate(new Date())}</p>
        <p>Reference: FNF-${fnf.employeeId}-${new Date().toISOString().substring(0, 10).replace(/-/g, '')}</p>
      </div>

      <div class="section">
        <div class="section-title">Employee Information</div>
        <table class="bordered">
          <tr>
            <td width="30%"><strong>Employee ID:</strong></td>
            <td>${fnf.employeeId}</td>
            <td width="30%"><strong>Department:</strong></td>
            <td>${fnf.departmentName}</td>
          </tr>
          <tr>
            <td><strong>Employee Name:</strong></td>
            <td>${fnf.employeeName}</td>
            <td><strong>Position:</strong></td>
            <td>${fnf.position || 'N/A'}</td>
          </tr>
          <tr>
            <td><strong>Separation Type:</strong></td>
            <td>
              <span class="badge ${
          fnf.separationType === 'Resignation' ? 'badge-success' :
              fnf.separationType === 'Death' ? 'badge-danger' :
                  fnf.separationType === 'Transfers' ? 'badge-primary' :
                      fnf.separationType === 'Dismissal' ? 'badge-warning' : 'badge-secondary'
      }">
                ${fnf.separationType}
              </span>
            </td>
            <td><strong>Last Working Day:</strong></td>
            <td>${formatDate(fnf.lastWorkingDay)}</td>
          </tr>
          <tr>
            <td><strong>FNF Status:</strong></td>
            <td>
              <span class="badge ${
          fnf.fnfStatus === 'Completed' ? 'badge-success' :
              fnf.fnfStatus === 'Processing' ? 'badge-primary' : 'badge-warning'
      }">
                ${fnf.fnfStatus}
              </span>
            </td>
            <td><strong>Settlement Date:</strong></td>
            <td>${fnf.settlementDate ? formatDate(fnf.settlementDate) : 'N/A'}</td>
          </tr>
        </table>
      </div>

      <div class="section">
        <div class="section-title">Earnings</div>
        <table>
          <thead>
            <tr>
              <th width="70%">Description</th>
              <th width="30%" class="amount">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Salary Due</td>
              <td class="amount">${formatCurrency(fnf.earnings.salaryDue)}</td>
            </tr>
            <tr>
              <td>Bonus Due</td>
              <td class="amount">${formatCurrency(fnf.earnings.bonusDue)}</td>
            </tr>
            <tr>
              <td>Leave Encashment</td>
              <td class="amount">${formatCurrency(fnf.earnings.leaveEncashment)}</td>
            </tr>
            <tr>
              <td>Gratuity</td>
              <td class="amount">${formatCurrency(fnf.earnings.gratuity)}</td>
            </tr>
            ${parseFloat(fnf.earnings.otherEarnings) > 0 ? `
            <tr>
              <td>Other Earnings ${fnf.earnings.notes ? `<small>(${fnf.earnings.notes})</small>` : ''}</td>
              <td class="amount">${formatCurrency(fnf.earnings.otherEarnings)}</td>
            </tr>
            ` : ''}
            <tr class="total-row">
              <td>Total Earnings</td>
              <td class="amount">${formatCurrency(fnf.totalEarnings)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="section">
        <div class="section-title">Deductions</div>
        <table>
          <thead>
            <tr>
              <th width="70%">Description</th>
              <th width="30%" class="amount">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Advance Salary</td>
              <td class="amount">${formatCurrency(fnf.deductions.advanceSalary)}</td>
            </tr>
            <tr>
              <td>Tax Deduction</td>
              <td class="amount">${formatCurrency(fnf.deductions.taxDeduction)}</td>
            </tr>
            <tr>
              <td>Loan Outstanding</td>
              <td class="amount">${formatCurrency(fnf.deductions.loan)}</td>
            </tr>
            <tr>
              <td>Recoveries (Assets)</td>
              <td class="amount">${formatCurrency(fnf.deductions.recoveries)}</td>
            </tr>
            ${parseFloat(fnf.deductions.otherDeductions) > 0 ? `
            <tr>
              <td>Other Deductions ${fnf.deductions.notes ? `<small>(${fnf.deductions.notes})</small>` : ''}</td>
              <td class="amount">${formatCurrency(fnf.deductions.otherDeductions)}</td>
            </tr>
            ` : ''}
            <tr class="total-row">
              <td>Total Deductions</td>
              <td class="amount">${formatCurrency(fnf.totalDeductions)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="section">
        <div class="section-title" style="text-align: center;">Settlement Summary</div>
        <table class="summary-table">
          <thead>
            <tr>
              <th width="70%">Description</th>
              <th width="30%" class="amount">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Total Earnings</td>
              <td class="amount">${formatCurrency(fnf.totalEarnings)}</td>
            </tr>
            <tr>
              <td>Total Deductions</td>
              <td class="amount">${formatCurrency(fnf.totalDeductions)}</td>
            </tr>
            <tr class="total-row">
              <td>Net Amount Payable</td>
              <td class="amount ${fnf.netPayable >= 0 ? 'positive' : 'negative'}">
                ${formatCurrency(fnf.netPayable)}
              </td>
            </tr>
            <tr>
              <td>Payment Method</td>
              <td class="amount">${formatPaymentMethod(fnf.paymentMethod)}</td>
            </tr>
            <tr>
              <td>Payment Status</td>
              <td class="amount">
                <span class="badge ${
          fnf.paymentStatus === 'Paid' ? 'badge-success' :
              fnf.paymentStatus === 'Due from Employee' ? 'badge-danger' : 'badge-warning'
      }">
                  ${fnf.paymentStatus}
                </span>
              </td>
            </tr>
            ${fnf.paymentDate ? `
            <tr>
              <td>Payment Date</td>
              <td class="amount">${formatDate(fnf.paymentDate)}</td>
            </tr>
            ` : ''}
            ${fnf.referenceNo ? `
            <tr>
              <td>Reference Number</td>
              <td class="amount">${fnf.referenceNo}</td>
            </tr>
            ` : ''}
          </tbody>
        </table>
      </div>

      ${fnf.notes ? `
      <div class="section">
        <div class="section-title">Notes</div>
        <div class="notes">
          ${fnf.notes}
        </div>
      </div>
      ` : ''}

      ${fnf.clearanceInfo ? `
      <div class="section ${!fnf.notes ? '' : 'page-break'}">
        <div class="section-title">Clearance Status</div>
        <table class="clearance-table">
          <tr>
            <td>
              <div class="info-box">
                <div class="label">Finance Department:</div>
                <div class="value">
                  <span class="badge ${fnf.clearanceInfo.financeClearance ? 'badge-success' : 'badge-warning'}">
                    ${fnf.clearanceInfo.financeClearance ? 'Cleared' : 'Pending'}
                  </span>
                </div>
              </div>
            </td>
            <td>
              <div class="info-box">
                <div class="label">IT Department:</div>
                <div class="value">
                  <span class="badge ${fnf.clearanceInfo.itClearance ? 'badge-success' : 'badge-warning'}">
                    ${fnf.clearanceInfo.itClearance ? 'Cleared' : 'Pending'}
                  </span>
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td>
              <div class="info-box">
                <div class="label">Admin Department:</div>
                <div class="value">
                  <span class="badge ${fnf.clearanceInfo.adminClearance ? 'badge-success' : 'badge-warning'}">
                    ${fnf.clearanceInfo.adminClearance ? 'Cleared' : 'Pending'}
                  </span>
                </div>
              </div>
            </td>
            <td>
              <div class="info-box">
                <div class="label">HR Department:</div>
                <div class="value">
                  <span class="badge ${fnf.clearanceInfo.hrClearance ? 'badge-success' : 'badge-warning'}">
                    ${fnf.clearanceInfo.hrClearance ? 'Cleared' : 'Pending'}
                  </span>
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td>
              <div class="info-box">
                <div class="label">Department Head:</div>
                <div class="value">
                  <span class="badge ${fnf.clearanceInfo.deptClearance ? 'badge-success' : 'badge-warning'}">
                    ${fnf.clearanceInfo.deptClearance ? 'Cleared' : 'Pending'}
                  </span>
                </div>
              </div>
            </td>
            <td>
              <div class="info-box">
                <div class="label">Security Department:</div>
                <div class="value">
                  <span class="badge ${fnf.clearanceInfo.securityClearance ? 'badge-success' : 'badge-warning'}">
                    ${fnf.clearanceInfo.securityClearance ? 'Cleared' : 'Pending'}
                  </span>
                </div>
              </div>
            </td>
          </tr>
        </table>
        ${fnf.clearanceInfo.clearanceNotes ? `
        <div style="margin-top: 10px; font-size: 12px;">
          <strong>Clearance Notes:</strong> ${fnf.clearanceInfo.clearanceNotes}
        </div>
        ` : ''}
      </div>
      ` : ''}

      ${fnf.approvals ? `
      <div class="section">
        <div class="section-title">Approvals</div>
        <table>
          <thead>
            <tr>
              <th>Department</th>
              <th>Status</th>
              <th>Approved By</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Finance</td>
              <td>
                <span class="badge ${fnf.approvals.finance ? 'badge-success' : 'badge-warning'}">
                  ${fnf.approvals.finance ? 'Approved' : 'Pending'}
                </span>
              </td>
              <td>${fnf.approvals.finance ? (fnf.approvals.financeBy || '-') : '-'}</td>
              <td>${fnf.approvals.finance && fnf.approvals.financeDate ? formatDate(fnf.approvals.financeDate) : '-'}</td>
            </tr>
            <tr>
              <td>HR</td>
              <td>
                <span class="badge ${fnf.approvals.hr ? 'badge-success' : 'badge-warning'}">
                  ${fnf.approvals.hr ? 'Approved' : 'Pending'}
                </span>
              </td>
              <td>${fnf.approvals.hr ? (fnf.approvals.hrBy || '-') : '-'}</td>
              <td>${fnf.approvals.hr && fnf.approvals.hrDate ? formatDate(fnf.approvals.hrDate) : '-'}</td>
            </tr>
            <tr>
              <td>Management</td>
              <td>
                <span class="badge ${fnf.approvals.management ? 'badge-success' : 'badge-warning'}">
                  ${fnf.approvals.management ? 'Approved' : 'Pending'}
                </span>
              </td>
              <td>${fnf.approvals.management ? (fnf.approvals.managementBy || '-') : '-'}</td>
              <td>${fnf.approvals.management && fnf.approvals.managementDate ? formatDate(fnf.approvals.managementDate) : '-'}</td>
            </tr>
          </tbody>
        </table>
      </div>
      ` : ''}

      <div class="signature-area">
        <div class="signature-box">
          <div class="signature-line">
            Human Resources Manager
          </div>
        </div>
        <div class="signature-box">
          <div class="signature-line">
            Employee Signature
          </div>
        </div>
      </div>

      <div class="footer">
        <p>This is a computer-generated document. No signature is required.</p>
        <p>Company Name - Full and Final Settlement</p>
      </div>

      <div class="no-print" style="text-align: center; margin-top: 20px;">
        <button onclick="window.print();" style="padding: 10px 20px; background-color: #4b6cb7; color: white; border: none; border-radius: 4px; cursor: pointer;">
          Print Document
        </button>
        <button onclick="window.close();" style="padding: 10px 20px; background-color: #666; color: white; border: none; border-radius: 4px; cursor: pointer; margin-left: 10px;">
          Close
        </button>
      </div>
    </body>
    </html>
  `;

      // Write to the new window and prepare for printing
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();

      // Add a small delay to ensure CSS is fully loaded
      setTimeout(() => {
        printWindow.focus();

        // Either automatically print or let user click the print button
        if (fnf.fnfStatus === 'Completed') {
          printWindow.print();
        }
      }, 500);
    },

    // Download FNF as PDF
    // Download FNF as PDF
    downloadPdf(fnf) {
      this.loading = true;

      try {
        // Import required libraries
        import('jspdf').then(({ default: jsPDF }) => {
          import('jspdf-autotable').then(({ default: autoTable }) => {
            // Create a new PDF document
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.width;

            // Add company logo if available
            // doc.addImage('/assets/images/logo.png', 'PNG', 15, 10, 50, 20);

            // Add title
            doc.setFontSize(18);
            doc.setTextColor(40, 40, 40);
            doc.text('FULL AND FINAL SETTLEMENT (FNF)', pageWidth / 2, 20, { align: 'center' });

            // Add report generation info
            doc.setFontSize(10);
            doc.setTextColor(100, 100, 100);
            doc.text(`Generated on: ${this.formatDate(new Date())}`, pageWidth - 15, 30, { align: 'right' });
            doc.text(`Reference: FNF-${fnf.employeeId}-${new Date().toISOString().substring(0, 10).replace(/-/g, '')}`, pageWidth - 15, 35, { align: 'right' });

            // Add employee information table
            doc.setFontSize(12);
            doc.setTextColor(40, 40, 40);
            doc.text('Employee Information', 15, 45);

            autoTable(doc, {
              startY: 50,
              head: [['Field', 'Value']],
              body: [
                ['Employee ID', fnf.employeeId],
                ['Employee Name', fnf.employeeName],
                ['Department', fnf.departmentName],
                ['Position', fnf.position || 'N/A'],
                ['Separation Type', fnf.separationType],
                ['Last Working Day', this.formatDate(fnf.lastWorkingDay)],
                ['Settlement Date', fnf.settlementDate ? this.formatDate(fnf.settlementDate) : 'N/A']
              ],
              theme: 'striped',
              headStyles: { fillColor: [66, 139, 202], textColor: 255 },
              columnStyles: {
                0: { cellWidth: 60, fontStyle: 'bold' },
                1: { cellWidth: 'auto' }
              },
              styles: { fontSize: 10, cellPadding: 5 }
            });

            // Add earnings details table
            const earningsY = doc.lastAutoTable.finalY + 15;
            doc.setFontSize(12);
            doc.text('Earnings', 15, earningsY);

            autoTable(doc, {
              startY: earningsY + 5,
              head: [['Description', 'Amount']],
              body: [
                ['Salary Due', this.formatCurrency(fnf.earnings.salaryDue)],
                ['Bonus Due', this.formatCurrency(fnf.earnings.bonusDue)],
                ['Leave Encashment', this.formatCurrency(fnf.earnings.leaveEncashment)],
                ['Gratuity', this.formatCurrency(fnf.earnings.gratuity)],
                fnf.earnings.otherEarnings > 0 ? ['Other Earnings', this.formatCurrency(fnf.earnings.otherEarnings)] : null,
                ['Total Earnings', this.formatCurrency(fnf.totalEarnings)]
              ].filter(Boolean), // Remove null entries
              theme: 'grid',
              headStyles: { fillColor: [46, 125, 50], textColor: 255 },
              columnStyles: {
                0: { cellWidth: 100, fontStyle: 'normal' },
                1: { cellWidth: 'auto', halign: 'right' }
              },
              styles: { fontSize: 10, cellPadding: 5 },
              // Format the total row with bold
              didParseCell: (data) => {
                if (data.section === 'body' && data.row.index === data.table.body.length - 1) {
                  data.cell.styles.fontStyle = 'bold';
                  data.cell.styles.fillColor = [240, 240, 240];
                }
              }
            });

            // Add deductions details table
            const deductionsY = doc.lastAutoTable.finalY + 15;

            // Check if we need a new page
            if (deductionsY > 220) {
              doc.addPage();
              doc.setFontSize(12);
              doc.text('Deductions', 15, 20);

              autoTable(doc, {
                startY: 25,
                head: [['Description', 'Amount']],
                body: [
                  ['Advance Salary', this.formatCurrency(fnf.deductions.advanceSalary)],
                  ['Tax Deduction', this.formatCurrency(fnf.deductions.taxDeduction)],
                  ['Loan Outstanding', this.formatCurrency(fnf.deductions.loan)],
                  ['Recoveries (Assets)', this.formatCurrency(fnf.deductions.recoveries)],
                  fnf.deductions.otherDeductions > 0 ? ['Other Deductions', this.formatCurrency(fnf.deductions.otherDeductions)] : null,
                  ['Total Deductions', this.formatCurrency(fnf.totalDeductions)]
                ].filter(Boolean), // Remove null entries
                theme: 'grid',
                headStyles: { fillColor: [183, 28, 28], textColor: 255 },
                columnStyles: {
                  0: { cellWidth: 100, fontStyle: 'normal' },
                  1: { cellWidth: 'auto', halign: 'right' }
                },
                styles: { fontSize: 10, cellPadding: 5 },
                // Format the total row with bold
                didParseCell: (data) => {
                  if (data.section === 'body' && data.row.index === data.table.body.length - 1) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [240, 240, 240];
                  }
                }
              });
            } else {
              doc.setFontSize(12);
              doc.text('Deductions', 15, deductionsY);

              autoTable(doc, {
                startY: deductionsY + 5,
                head: [['Description', 'Amount']],
                body: [
                  ['Advance Salary', this.formatCurrency(fnf.deductions.advanceSalary)],
                  ['Tax Deduction', this.formatCurrency(fnf.deductions.taxDeduction)],
                  ['Loan Outstanding', this.formatCurrency(fnf.deductions.loan)],
                  ['Recoveries (Assets)', this.formatCurrency(fnf.deductions.recoveries)],
                  fnf.deductions.otherDeductions > 0 ? ['Other Deductions', this.formatCurrency(fnf.deductions.otherDeductions)] : null,
                  ['Total Deductions', this.formatCurrency(fnf.totalDeductions)]
                ].filter(Boolean), // Remove null entries
                theme: 'grid',
                headStyles: { fillColor: [183, 28, 28], textColor: 255 },
                columnStyles: {
                  0: { cellWidth: 100, fontStyle: 'normal' },
                  1: { cellWidth: 'auto', halign: 'right' }
                },
                styles: { fontSize: 10, cellPadding: 5 },
                // Format the total row with bold
                didParseCell: (data) => {
                  if (data.section === 'body' && data.row.index === data.table.body.length - 1) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [240, 240, 240];
                  }
                }
              });
            }

            // Add settlement summary
            const summaryY = doc.lastAutoTable.finalY + 15;

            // Check if we need a new page
            if (summaryY > 220) {
              doc.addPage();
              doc.setFontSize(14);
              doc.setTextColor(40, 40, 40);
              doc.text('Settlement Summary', pageWidth / 2, 20, { align: 'center' });

              autoTable(doc, {
                startY: 30,
                head: [['Description', 'Amount']],
                body: [
                  ['Total Earnings', this.formatCurrency(fnf.totalEarnings)],
                  ['Total Deductions', this.formatCurrency(fnf.totalDeductions)],
                  ['Net Payable', this.formatCurrency(fnf.netPayable)]
                ],
                theme: 'grid',
                headStyles: { fillColor: [66, 139, 202], textColor: 255 },
                columnStyles: {
                  0: { cellWidth: 100, fontStyle: 'bold' },
                  1: { cellWidth: 'auto', halign: 'right' }
                },
                styles: { fontSize: 10, cellPadding: 5 },
                // Format the net payable row
                didParseCell: (data) => {
                  if (data.section === 'body' && data.row.index === 2) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fontSize = 12;
                  }
                },
                // Color the net payable cell based on the value
                didDrawCell: (data) => {
                  if (data.section === 'body' && data.row.index === 2 && data.column.index === 1) {
                    if (fnf.netPayable >= 0) {
                      doc.setFillColor(220, 255, 220); // Light green for positive
                    } else {
                      doc.setFillColor(255, 220, 220); // Light red for negative
                    }
                    doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
                    doc.setTextColor(0);
                    doc.text(this.formatCurrency(fnf.netPayable), data.cell.x + data.cell.width - 5, data.cell.y + 10, { align: 'right' });
                  }
                }
              });

              // Add payment information
              const paymentY = doc.lastAutoTable.finalY + 15;
              doc.setFontSize(12);
              doc.text('Payment Information', 15, paymentY);

              autoTable(doc, {
                startY: paymentY + 5,
                body: [
                  ['Payment Status', fnf.paymentStatus],
                  ['Payment Method', this.formatPaymentMethod(fnf.paymentMethod)],
                  fnf.paymentDate ? ['Payment Date', this.formatDate(fnf.paymentDate)] : null,
                  fnf.referenceNo ? ['Reference Number', fnf.referenceNo] : null
                ].filter(Boolean), // Remove null entries
                theme: 'plain',
                columnStyles: {
                  0: { cellWidth: 100, fontStyle: 'bold' },
                  1: { cellWidth: 'auto' }
                },
                styles: { fontSize: 10, cellPadding: 5 }
              });
            } else {
              doc.setFontSize(14);
              doc.setTextColor(40, 40, 40);
              doc.text('Settlement Summary', pageWidth / 2, summaryY, { align: 'center' });

              autoTable(doc, {
                startY: summaryY + 10,
                head: [['Description', 'Amount']],
                body: [
                  ['Total Earnings', this.formatCurrency(fnf.totalEarnings)],
                  ['Total Deductions', this.formatCurrency(fnf.totalDeductions)],
                  ['Net Payable', this.formatCurrency(fnf.netPayable)]
                ],
                theme: 'grid',
                headStyles: { fillColor: [66, 139, 202], textColor: 255 },
                columnStyles: {
                  0: { cellWidth: 100, fontStyle: 'bold' },
                  1: { cellWidth: 'auto', halign: 'right' }
                },
                styles: { fontSize: 10, cellPadding: 5 },
                // Format the net payable row
                didParseCell: (data) => {
                  if (data.section === 'body' && data.row.index === 2) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fontSize = 12;
                  }
                },
                // Color the net payable cell based on the value
                didDrawCell: (data) => {
                  if (data.section === 'body' && data.row.index === 2 && data.column.index === 1) {
                    if (fnf.netPayable >= 0) {
                      doc.setFillColor(220, 255, 220); // Light green for positive
                    } else {
                      doc.setFillColor(255, 220, 220); // Light red for negative
                    }
                    doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
                    doc.setTextColor(0);
                    doc.text(this.formatCurrency(fnf.netPayable), data.cell.x + data.cell.width - 5, data.cell.y + 10, { align: 'right' });
                  }
                }
              });

              // Add payment information
              const paymentY = doc.lastAutoTable.finalY + 15;
              doc.setFontSize(12);
              doc.text('Payment Information', 15, paymentY);

              autoTable(doc, {
                startY: paymentY + 5,
                body: [
                  ['Payment Status', fnf.paymentStatus],
                  ['Payment Method', this.formatPaymentMethod(fnf.paymentMethod)],
                  fnf.paymentDate ? ['Payment Date', this.formatDate(fnf.paymentDate)] : null,
                  fnf.referenceNo ? ['Reference Number', fnf.referenceNo] : null
                ].filter(Boolean), // Remove null entries
                theme: 'plain',
                columnStyles: {
                  0: { cellWidth: 100, fontStyle: 'bold' },
                  1: { cellWidth: 'auto' }
                },
                styles: { fontSize: 10, cellPadding: 5 }
              });
            }

            // Add notes if available
            if (fnf.notes) {
              const notesY = doc.lastAutoTable.finalY + 15;

              // Check if we need a new page
              if (notesY > 240) {
                doc.addPage();
                doc.setFontSize(12);
                doc.text('Notes', 15, 20);

                doc.setFontSize(10);
                doc.setTextColor(60, 60, 60);
                doc.text(fnf.notes, 15, 30, {
                  maxWidth: pageWidth - 30,
                  align: 'left'
                });
              } else {
                doc.setFontSize(12);
                doc.text('Notes', 15, notesY);

                doc.setFontSize(10);
                doc.setTextColor(60, 60, 60);
                doc.text(fnf.notes, 15, notesY + 10, {
                  maxWidth: pageWidth - 30,
                  align: 'left'
                });
              }
            }

            // Add approvals section if there are any approvals
            if (fnf.approvals) {
              const approvalsY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : 220;

              // Check if we need a new page
              if (approvalsY > 240) {
                doc.addPage();
                doc.setFontSize(12);
                doc.text('Approvals', 15, 20);

                autoTable(doc, {
                  startY: 25,
                  head: [['Department', 'Status', 'Approved By', 'Date']],
                  body: [
                    fnf.approvals.finance ? ['Finance', 'Approved', fnf.approvals.financeBy || '-', fnf.approvals.financeDate ? this.formatDate(fnf.approvals.financeDate) : '-'] : ['Finance', 'Pending', '-', '-'],
                    fnf.approvals.hr ? ['HR', 'Approved', fnf.approvals.hrBy || '-', fnf.approvals.hrDate ? this.formatDate(fnf.approvals.hrDate) : '-'] : ['HR', 'Pending', '-', '-'],
                    fnf.approvals.management ? ['Management', 'Approved', fnf.approvals.managementBy || '-', fnf.approvals.managementDate ? this.formatDate(fnf.approvals.managementDate) : '-'] : ['Management', 'Pending', '-', '-']
                  ],
                  theme: 'striped',
                  headStyles: { fillColor: [66, 139, 202], textColor: 255 },
                  styles: { fontSize: 10, cellPadding: 5 }
                });
              } else {
                doc.setFontSize(12);
                doc.text('Approvals', 15, approvalsY);

                autoTable(doc, {
                  startY: approvalsY + 5,
                  head: [['Department', 'Status', 'Approved By', 'Date']],
                  body: [
                    fnf.approvals.finance ? ['Finance', 'Approved', fnf.approvals.financeBy || '-', fnf.approvals.financeDate ? this.formatDate(fnf.approvals.financeDate) : '-'] : ['Finance', 'Pending', '-', '-'],
                    fnf.approvals.hr ? ['HR', 'Approved', fnf.approvals.hrBy || '-', fnf.approvals.hrDate ? this.formatDate(fnf.approvals.hrDate) : '-'] : ['HR', 'Pending', '-', '-'],
                    fnf.approvals.management ? ['Management', 'Approved', fnf.approvals.managementBy || '-', fnf.approvals.managementDate ? this.formatDate(fnf.approvals.managementDate) : '-'] : ['Management', 'Pending', '-', '-']
                  ],
                  theme: 'striped',
                  headStyles: { fillColor: [66, 139, 202], textColor: 255 },
                  styles: { fontSize: 10, cellPadding: 5 }
                });
              }
            }

            // Add clearance section if available
            if (fnf.clearanceInfo) {
              const clearanceY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : 220;

              // Check if we need a new page
              if (clearanceY > 240) {
                doc.addPage();
                doc.setFontSize(12);
                doc.text('Clearance Status', 15, 20);

                autoTable(doc, {
                  startY: 25,
                  head: [['Department', 'Status']],
                  body: [
                    ['Finance', fnf.clearanceInfo.financeClearance ? 'Cleared' : 'Pending'],
                    ['IT', fnf.clearanceInfo.itClearance ? 'Cleared' : 'Pending'],
                    ['Admin', fnf.clearanceInfo.adminClearance ? 'Cleared' : 'Pending'],
                    ['HR', fnf.clearanceInfo.hrClearance ? 'Cleared' : 'Pending'],
                    ['Department Head', fnf.clearanceInfo.deptClearance ? 'Cleared' : 'Pending'],
                    ['Security', fnf.clearanceInfo.securityClearance ? 'Cleared' : 'Pending']
                  ],
                  theme: 'striped',
                  headStyles: { fillColor: [66, 139, 202], textColor: 255 },
                  styles: { fontSize: 10, cellPadding: 5 }
                });
              } else {
                doc.setFontSize(12);
                doc.text('Clearance Status', 15, clearanceY);

                autoTable(doc, {
                  startY: clearanceY + 5,
                  head: [['Department', 'Status']],
                  body: [
                    ['Finance', fnf.clearanceInfo.financeClearance ? 'Cleared' : 'Pending'],
                    ['IT', fnf.clearanceInfo.itClearance ? 'Cleared' : 'Pending'],
                    ['Admin', fnf.clearanceInfo.adminClearance ? 'Cleared' : 'Pending'],
                    ['HR', fnf.clearanceInfo.hrClearance ? 'Cleared' : 'Pending'],
                    ['Department Head', fnf.clearanceInfo.deptClearance ? 'Cleared' : 'Pending'],
                    ['Security', fnf.clearanceInfo.securityClearance ? 'Cleared' : 'Pending']
                  ],
                  theme: 'striped',
                  headStyles: { fillColor: [66, 139, 202], textColor: 255 },
                  styles: { fontSize: 10, cellPadding: 5 }
                });
              }
            }

            // Add footer to all pages
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
              doc.setPage(i);

              // Footer line
              doc.setDrawColor(200, 200, 200);
              doc.line(15, 280, pageWidth - 15, 280);

              // Footer text
              doc.setFontSize(8);
              doc.setTextColor(100, 100, 100);
              doc.text('Company Name - Full and Final Settlement', 15, 285);
              doc.text(`Page ${i} of ${pageCount}`, pageWidth - 15, 285, { align: 'right' });
            }

            // Save the PDF
            const fileName = `FNF_Settlement_${fnf.employeeId}_${new Date().toISOString().substring(0, 10).replace(/-/g, '')}.pdf`;
            doc.save(fileName);

            // Show success message
            this.$toast ? this.$toast.success('PDF report generated successfully') : alert('PDF report generated successfully');
          }).catch(error => {
            console.error('Error loading jspdf-autotable:', error);
            this.message = 'Error generating PDF: Could not load required libraries';
          });
        }).catch(error => {
          console.error('Error loading jsPDF:', error);
          this.message = 'Error generating PDF: Could not load required libraries';
        }).finally(() => {
          this.loading = false;
        });
      } catch (error) {
        console.error('Error generating PDF:', error);
        this.message = 'Error generating PDF: ' + error.message;
        this.loading = false;
      }
    },
    // Email FNF settlement to employee
    // Add this method to your Vue component
    emailSettlement(fnf) {
      // Create modal for email options
      if (!this.emailModal) {
        this.emailModal = new bootstrap.Modal(document.getElementById('modal-email-fnf'));
      }

      this.selectedFnf = fnf;
      this.emailForm = {
        template: 'default',
        includeAttachment: true,
        ccToManager: false,
        sending: false,
        success: false,
        error: ''
      };

      this.emailModal.show();
    },

// Method to actually send the email after selecting options
    sendFnfEmail() {
      if (!this.selectedFnf) return;

      this.emailForm.sending = true;
      this.emailForm.success = false;
      this.emailForm.error = '';

      const data = new FormData();
      data.append("function", "emailFnfSettlement");
      data.append("fnfId", this.selectedFnf.id);
      data.append("emailTemplate", this.emailForm.template);

      if (this.emailForm.includeAttachment) {
        data.append("includeAttachment", "1");
      }

      if (this.emailForm.ccToManager) {
        data.append("ccToManager", "1");
      }

      execute(data, authUrl)
          .then((res) => {
            this.emailForm.sending = false;

            if (res.data.success) {
              this.emailForm.success = true;
              this.message = `Settlement emailed successfully to ${res.data.data?.emailAddress || 'employee'}`;

              // Update the FNF record's email sent status in the local data
              const fnfIndex = this.fnfData.findIndex(item => item.id === this.selectedFnf.id);
              if (fnfIndex !== -1) {
                this.fnfData[fnfIndex].emailSent = true;
                this.fnfData[fnfIndex].emailSentDate = new Date().toISOString();
              }

              // Close modal after a delay
              setTimeout(() => {
                this.emailModal.hide();
              }, 2000);
            } else {
              this.emailForm.error = res.data.message || "Failed to email settlement";
              this.message = this.emailForm.error;
            }
          })
          .catch((e) => {
            this.emailForm.sending = false;
            this.emailForm.error = e.message || "An error occurred while sending email";
            this.message = this.emailForm.error;
          });
    },
    // Export FNF report
    exportReport() {
      this.message = "Report export functionality will be implemented soon...";
      // In real implementation:
      // const data = new FormData();
      // data.append("function", "exportFnfReport");
      //
      // if (this.selectedMonth) {
      //   data.append("month", this.selectedMonth);
      // }
      //
      // if (this.selectedDepartment) {
      //   data.append("department", this.selectedDepartment);
      // }
      //
      // execute(data, authUrl)
      //   .then((res) => {
      //     if (res.data.success) {
      //       // Handle export (e.g., window.open(res.data.data.exportUrl))
      //     } else {
      //       this.message = res.data.message || "Failed to export report";
      //     }
      //   });
    },

    // Format date for display
    formatDate(dateString) {
      if (!dateString) return "--";
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    // Format currency for display
    // Format currency for display
    // Format payment method for display
    formatPaymentMethod(method) {
      if (!method) return "N/A";

      switch(method.toLowerCase()) {
        case 'bank':
          return "Bank Transfer";
        case 'check':
          return "Check";
        case 'cash':
          return "Cash";
        default:
          return method;
      }
    },

    // Get current currency code based on selected department
    getCurrentCurrencyCode() {
      let currencyCode = '';

      if (this.selectedDepartment) {
        // Find the selected department in departments array
        const dept = this.departments.find(d => d.id === this.selectedDepartment);
        if (dept && dept.currency) {
          currencyCode = dept.currency;
        }
      }

      // If no department is selected or found, use the first department's currency
      if (!currencyCode && this.departments.length > 0 && this.departments[0].currency) {
        currencyCode = this.departments[0].currency;
      }

      return currencyCode || 'USD'; // Fallback to USD if no currency found
    },

// Get currency symbol for display in input fields
    getCurrencySymbol() {
      const currencyCode = this.getCurrentCurrencyCode();
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

      // Extract just the symbol
      const symbol = formatter.format(0).replace(/\d/g, '').trim();
      return symbol;
    },

// Format currency for display
    formatCurrency(value) {
      if (!value) return "0.00";
      const numValue = parseFloat(value);
      const currencyCode = this.getCurrentCurrencyCode();

      // Format the number with the appropriate currency
      return numValue.toLocaleString('en-US', {
        style: 'currency',
        currency: currencyCode
      });
    },

    // Open the FNF approval modal
    openApprovalModal(fnf) {
      this.selectedFnf = fnf;
      this.activeApprovalTab = 'finance';
      this.approvalForm = {
        notes: '',
        approverName: ''
      };

      // Initialize the modal if it doesn't exist
      if (!this.approvalModal) {
        this.approvalModal = new bootstrap.Modal(document.getElementById('modal-fnf-approval'));
      }

      this.approvalModal.show();
    },

    // Finance approval
    approveFinance() {
      if (!this.selectedFnf) return;
      this.isApproving = true;

      // Create approvals object if it doesn't exist
      const approvals = this.selectedFnf.approvals || {};

      // Update the approvals
      const updatedApprovals = {
        ...approvals,
        finance: true,
        financeBy: "Current User", // Replace with actual user name
        financeDate: new Date().toISOString()
      };

      // Prepare data for API call
      const data = new FormData();
      data.append("function", "updateFnfApproval");
      data.append("fnfId", this.selectedFnf.id);
      data.append("approvalType", "finance");
      data.append("approvals", JSON.stringify(updatedApprovals));
      data.append("notes", this.approvalForm.notes);

      execute(data, authUrl)
          .then((res) => {
            this.isApproving = false;
            if (res.data.success) {
              // Update the local record
              this.selectedFnf.approvals = updatedApprovals;
              this.message = "Finance approval successful";

              // Refresh the FNF data
              this.getFnfData();
            } else {
              this.message = res.data.message || "Failed to update approval status";
            }
          })
          .catch((e) => {
            this.isApproving = false;
            this.message = e.message || "An error occurred while updating approval";
          });
    },

    // HR approval
    approveHR() {
      if (!this.selectedFnf) return;

      // Check if finance is approved
      if (!(this.selectedFnf.approvals && this.selectedFnf.approvals.finance)) {
        this.message = "Finance approval is required before HR approval";
        return;
      }

      this.isApproving = true;

      // Update the approvals
      const updatedApprovals = {
        ...this.selectedFnf.approvals,
        hr: true,
        hrBy: "Current User", // Replace with actual user name
        hrDate: new Date().toISOString()
      };

      // Prepare data for API call
      const data = new FormData();
      data.append("function", "updateFnfApproval");
      data.append("fnfId", this.selectedFnf.id);
      data.append("approvalType", "hr");
      data.append("approvals", JSON.stringify(updatedApprovals));
      data.append("notes", this.approvalForm.notes);

      execute(data, authUrl)
          .then((res) => {
            this.isApproving = false;
            if (res.data.success) {
              // Update the local record
              this.selectedFnf.approvals = updatedApprovals;
              this.message = "HR approval successful";

              // Refresh the FNF data
              this.getFnfData();
            } else {
              this.message = res.data.message || "Failed to update approval status";
            }
          })
          .catch((e) => {
            this.isApproving = false;
            this.message = e.message || "An error occurred while updating approval";
          });
    },

    // Management approval
    approveManagement() {
      if (!this.selectedFnf) return;

      // Check if finance and HR are approved
      if (!(this.selectedFnf.approvals && this.selectedFnf.approvals.finance && this.selectedFnf.approvals.hr)) {
        this.message = "Both Finance and HR approvals are required before Management approval";
        return;
      }

      this.isApproving = true;

      // Update the approvals
      const updatedApprovals = {
        ...this.selectedFnf.approvals,
        management: true,
        managementBy: "Current User", // Replace with actual user name
        managementDate: new Date().toISOString()
      };

      // Prepare data for API call
      const data = new FormData();
      data.append("function", "updateFnfApproval");
      data.append("fnfId", this.selectedFnf.id);
      data.append("approvalType", "management");
      data.append("approvals", JSON.stringify(updatedApprovals));
      data.append("notes", this.approvalForm.notes);

      execute(data, authUrl)
          .then((res) => {
            this.isApproving = false;
            if (res.data.success) {
              // Update the local record
              this.selectedFnf.approvals = updatedApprovals;
              this.message = "Management approval successful";

              // Refresh the FNF data
              this.getFnfData();
            } else {
              this.message = res.data.message || "Failed to update approval status";
            }
          })
          .catch((e) => {
            this.isApproving = false;
            this.message = e.message || "An error occurred while updating approval";
          });
    },

    // Complete FNF after all approvals
    completeFnfAfterApproval() {
      if (!this.selectedFnf || !this.approvalModal) return;

      // Close the approval modal
      this.approvalModal.hide();

      // Check if all approvals are in place
      if (
          this.selectedFnf.approvals &&
          this.selectedFnf.approvals.finance &&
          this.selectedFnf.approvals.hr &&
          this.selectedFnf.approvals.management
      ) {
        // Complete the FNF process
        this.completeFnf(this.selectedFnf);
      } else {
        this.message = "All approvals are required to complete the FNF process";
      }
    }

  }
};
</script>
<style src="@/assets/css/hr-page-shared.css"></style>
<style scoped>
.fnf-month-select { min-width: 140px; margin-right: 8px; }
.sep-toast { position: fixed; bottom: 20px; right: 20px; z-index: 1100; padding: 12px 20px; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.15); display: flex; align-items: center; gap: 12px; max-width: 400px; }
.sep-toast.success { background: #10b981; color: #fff; }
.sep-toast.error { background: #ef4444; color: #fff; }
.sep-toast-close { background: none; border: none; color: inherit; font-size: 20px; cursor: pointer; padding: 0 4px; }
.sep-filters { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; flex: 1; }
.sep-filters .hr-search-wrap { min-width: 200px; }
.hr-flex-between { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; }
.hr-loading-wrap { text-align: center; padding: 3rem; }
.hr-spinner { width: 40px; height: 40px; border: 3px solid rgba(91,115,232,0.2); border-top-color: #5b73e8; border-radius: 50%; animation: fnf-spin 0.8s linear infinite; margin: 0 auto 1rem; }
.fnf-status-cell { display: flex; align-items: center; gap: 6px; }
.fnf-status-icon { font-size: 1.1rem; }
.fnf-status-pending { color: #f59e0b; }
.fnf-status-approved { color: #10b981; }
.fnf-status-completed { color: #10b981; }
.fnf-status-cancelled { color: #ef4444; }
.fnf-amount { font-weight: 600; }
.payment-positive { color: #047857; }
.payment-negative { color: #b91c1c; }
.fnf-actions-cell { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.hr-btn-ghost { background: transparent; border: 1px solid #e2e8f0; color: #64748b; }
.hr-btn-ghost:hover { background: #f1f5f9; }
.hr-small { font-size: 0.85rem; }
.hr-mt-1 { margin-top: 4px; }
.hr-py-4 { padding-top: 16px; padding-bottom: 16px; }
.hr-table-striped tbody tr:nth-child(even) { background: #f8fafc; }
.hr-table-hover tbody tr:hover { background: rgba(91,115,232,0.05); }
@keyframes fnf-spin { to { transform: rotate(360deg); } }
</style>